import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BarChart3,
  BookmarkPlus,
  CheckCircle2,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  Globe2,
  History as HistoryIcon,
  Loader2,
  Monitor,
  Palette,
  Plus,
  RefreshCw,
  RotateCcw,
  Smartphone,
  ShieldCheck,
  Tablet,
  Trash2,
  Users,
  X,
} from "lucide-react";

import { API, useAuth } from "../App";
import EmbeddedAdminLayout from "../components/EmbeddedAdminLayout";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import WebsiteBlockEditor, {
  BLOCK_DEFINITIONS,
  starterProps,
} from "../components/website/WebsiteBlockEditor";
import { buildWebsiteLaunchReport } from "../utils/websiteLaunchReadiness";

const BLOCK_LABELS = Object.fromEntries(
  Object.entries(BLOCK_DEFINITIONS).map(([key, value]) => [key, value.label]),
);

const BLOCK_LIBRARY = [
  {
    category: "Essentials",
    description: "Build the page structure and guide visitors.",
    blocks: {
      hero: "Lead with a strong headline and optional photo.",
      rich_text: "Add an accessible long-form text section.",
      image_text: "Pair a story or announcement with a photo.",
      cta: "Guide visitors to one important next step.",
      navigation: "Add links to the most important pages.",
      footer: "Close the page with useful links and details.",
    },
  },
  {
    category: "Programs & events",
    description: "Promote registration, schedules, and participation.",
    blocks: {
      program_grid: "Show a curated group of available programs.",
      featured_program: "Give one program extra prominence.",
      upcoming_events: "Highlight what is coming up next.",
      registration_cta: "Send families directly to registration.",
    },
  },
  {
    category: "Community",
    description: "Build trust with the people behind the organization.",
    blocks: {
      staff: "Introduce coaches, staff, and board members.",
      sponsors: "Recognize the partners supporting your club.",
      testimonials: "Share stories from players and families.",
      faq: "Answer common questions before families ask.",
    },
  },
  {
    category: "Contact & location",
    description: "Help visitors reach and find the organization.",
    blocks: {
      contact_form: "Collect a structured message from visitors.",
      location_contact: "Show approved location and contact details.",
    },
  },
];

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

const DEFAULT_THEME = {
  primary_color: "#24766f",
  primary_text_color: "#ffffff",
  background_color: "#f6f7f4",
  text_color: "#101a19",
  accent_color: "#c8ff45",
  heading_font: "system",
  body_font: "system",
  button_style: "pill",
  spacing: "comfortable",
  content_width: "standard",
};

const FONT_STACKS = {
  system: "Inter, ui-sans-serif, system-ui, sans-serif",
  editorial: "Georgia, Cambria, 'Times New Roman', serif",
  rounded: "ui-rounded, 'Arial Rounded MT Bold', system-ui, sans-serif",
};

const themeStyle = (value = {}) => {
  const theme = { ...DEFAULT_THEME, ...value };
  return {
    backgroundColor: theme.background_color,
    color: theme.text_color,
    fontFamily: FONT_STACKS[theme.body_font],
    "--website-primary": theme.primary_color,
    "--website-primary-text": theme.primary_text_color,
    "--website-accent": theme.accent_color,
    "--website-heading-font": FONT_STACKS[theme.heading_font],
    "--website-radius":
      theme.button_style === "pill"
        ? "9999px"
        : theme.button_style === "rounded"
          ? "0.75rem"
          : "0.125rem",
  };
};

const ThemeControls = ({ value, onSave, onPreview, saving }) => {
  const [theme, setTheme] = useState({ ...DEFAULT_THEME, ...(value || {}) });
  useEffect(
    () => setTheme({ ...DEFAULT_THEME, ...(value || {}) }),
    [value],
  );
  const update = (key, next) => {
    const updated = { ...theme, [key]: next };
    setTheme(updated);
    onPreview(updated);
  };
  return (
    <form
      className="mt-3 space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(theme);
      }}
    >
      <fieldset className="grid grid-cols-2 gap-2">
        <legend className="sr-only">Brand colors</legend>
        {[
          ["primary_color", "Primary"],
          ["primary_text_color", "On primary"],
          ["background_color", "Background"],
          ["text_color", "Text"],
          ["accent_color", "Accent"],
        ].map(([key, label]) => (
          <label key={key} className="rounded-xl border border-[#dce4dc] bg-white p-2 text-[10px] font-bold uppercase tracking-wider text-[#60706a]">
            {label}
            <input aria-label={`${label} color`} type="color" value={theme[key]} onChange={(event) => update(key, event.target.value)} className="mt-1 h-8 w-full cursor-pointer rounded border-0 bg-transparent p-0" />
          </label>
        ))}
      </fieldset>
      {[
        ["heading_font", "Heading font", [["system", "Modern"], ["editorial", "Editorial"], ["rounded", "Friendly"]]],
        ["body_font", "Body font", [["system", "Modern"], ["editorial", "Editorial"], ["rounded", "Friendly"]]],
        ["button_style", "Button shape", [["rounded", "Rounded"], ["square", "Square"], ["pill", "Pill"]]],
        ["spacing", "Section spacing", [["compact", "Compact"], ["comfortable", "Comfortable"], ["spacious", "Spacious"]]],
        ["content_width", "Content width", [["standard", "Standard"], ["wide", "Wide"]]],
      ].map(([key, label, options]) => (
        <label key={key} className="block text-xs font-bold text-[#42534d]">
          {label}
          <select value={theme[key]} onChange={(event) => update(key, event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border border-[#cbd6ce] bg-white px-3 text-sm">
            {options.map(([option, name]) => <option key={option} value={option}>{name}</option>)}
          </select>
        </label>
      ))}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={saving}>Save theme</Button>
        <Button type="button" size="sm" variant="outline" disabled={saving} onClick={() => { setTheme(DEFAULT_THEME); onPreview(DEFAULT_THEME); onSave(DEFAULT_THEME); }}>Reset</Button>
      </div>
    </form>
  );
};

const friendlyWebsiteError = (error, fallback) => {
  const detail = error.response?.data?.detail;
  const code = typeof detail === "object" ? detail?.code : detail;
  const messages = {
    invalid_block_props:
      "Some section details are incomplete. Open the highlighted section and correct the fields marked below.",
    stale_revision:
      "This website changed in another session. Reload to keep the newest version.",
    website_has_no_pages: "Add at least one page before publishing.",
    page_identity_conflict:
      "That page address is already in use. Choose a different page name.",
  };
  return messages[code] || fallback;
};

const appearanceStyle = (appearance = {}) => ({
  ...(appearance.background_color
    ? { backgroundColor: appearance.background_color }
    : {}),
  ...(appearance.text_color ? { color: appearance.text_color } : {}),
  ...(appearance.accent_color
    ? {
        "--website-accent": appearance.accent_color,
        borderColor: appearance.accent_color,
      }
    : {}),
});

const AppearanceControls = ({ value = {}, onChange, label }) => (
  <fieldset className="grid grid-cols-3 gap-2">
    <legend className="sr-only">{label} colors</legend>
    {[
      ["background_color", "Background", "#f7f4eb"],
      ["text_color", "Text", "#102a27"],
      ["accent_color", "Accent", "#b7f34a"],
    ].map(([key, name, fallback]) => (
      <label
        key={key}
        className="rounded-xl border border-[#dce4dc] bg-white p-2 text-[10px] font-bold uppercase tracking-wider text-[#60706a]"
      >
        {name}
        <span className="mt-1 flex items-center gap-2">
          <input
            type="color"
            value={value[key] || fallback}
            onChange={(event) =>
              onChange({ ...value, [key]: event.target.value })
            }
            className="h-8 w-full cursor-pointer rounded border-0 bg-transparent p-0"
            aria-label={`${label} ${name.toLowerCase()} color`}
          />
        </span>
      </label>
    ))}
  </fieldset>
);

const MobileAppearanceControls = ({ value = {}, onChange, label }) => (
  <fieldset className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
    <legend className="sr-only">{label} mobile layout</legend>
    <label className="text-xs font-bold text-[#42534d]">
      Text alignment
      <select
        aria-label={`${label} mobile text alignment`}
        value={value.mobile_alignment || "inherit"}
        onChange={(event) =>
          onChange({ ...value, mobile_alignment: event.target.value })
        }
        className="mt-1 min-h-11 w-full rounded-xl border border-[#cbd6ce] bg-white px-3 text-sm"
      >
        <option value="inherit">Use desktop setting</option>
        <option value="left">Left</option>
        <option value="center">Center</option>
      </select>
    </label>
    <label className="text-xs font-bold text-[#42534d]">
      Section spacing
      <select
        aria-label={`${label} mobile section spacing`}
        value={value.mobile_spacing || "inherit"}
        onChange={(event) =>
          onChange({ ...value, mobile_spacing: event.target.value })
        }
        className="mt-1 min-h-11 w-full rounded-xl border border-[#cbd6ce] bg-white px-3 text-sm"
      >
        <option value="inherit">Use site setting</option>
        <option value="compact">Compact</option>
        <option value="comfortable">Comfortable</option>
        <option value="spacious">Spacious</option>
      </select>
    </label>
    <label className="text-xs font-bold text-[#42534d]">
      Mobile visibility
      <select
        aria-label={`${label} mobile visibility`}
        value={value.mobile_visibility || "visible"}
        onChange={(event) =>
          onChange({ ...value, mobile_visibility: event.target.value })
        }
        className="mt-1 min-h-11 w-full rounded-xl border border-[#cbd6ce] bg-white px-3 text-sm"
      >
        <option value="visible">Show</option>
        <option value="hidden">Hide on mobile</option>
      </select>
    </label>
  </fieldset>
);

const previewAppearanceStyle = (appearance = {}, viewport) => ({
  ...appearanceStyle(appearance),
  ...(viewport === "mobile" && appearance.mobile_alignment !== "inherit"
    ? { textAlign: appearance.mobile_alignment }
    : {}),
});

const previewSectionPadding = (appearance = {}, viewport) => {
  if (viewport !== "mobile") return viewport === "tablet" ? "p-8" : "p-10";
  return {
    compact: "px-5 py-8",
    spacious: "px-6 py-16",
  }[appearance.mobile_spacing] || "p-6";
};

const WebsiteInquiryInbox = ({ orgId, token }) => {
  const [state, setState] = useState({ status: "idle", items: [], message: "" });
  const load = async () => {
    setState((current) => ({ ...current, status: "loading", message: "" }));
    try {
      const response = await axios.get(
        `${API}/organizations/${orgId}/website/inquiries`,
        { headers: authHeaders(token) },
      );
      setState({ status: "ready", items: response.data.items || [], message: "" });
    } catch (_error) {
      setState((current) => ({
        ...current,
        status: "error",
        message: "Inquiries could not be loaded. Try again.",
      }));
    }
  };
  const updateStatus = async (inquiryId, status) => {
    try {
      const response = await axios.patch(
        `${API}/organizations/${orgId}/website/inquiries/${inquiryId}`,
        { status },
        { headers: authHeaders(token) },
      );
      setState((current) => ({
        ...current,
        items: current.items.map((item) =>
          item.inquiry_id === inquiryId ? response.data : item,
        ),
        message: "",
      }));
    } catch (_error) {
      setState((current) => ({
        ...current,
        message: "Inquiry status could not be updated.",
      }));
    }
  };
  const newCount = state.items.filter((item) => item.status === "new").length;
  return (
    <details
      className="mt-4 rounded-2xl border border-[#c8d4ca] bg-white p-3"
      onToggle={(event) => {
        if (event.currentTarget.open && state.status === "idle") load();
      }}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-[#344740]">
        <span>Website inquiries</span>
        {newCount > 0 && (
          <span className="rounded-full bg-[#102a27] px-2 py-1 text-[10px] text-white">
            {newCount} new
          </span>
        )}
      </summary>
      {state.status === "loading" && (
        <p className="mt-3 text-sm text-[#60706a]" role="status">Loading inquiries…</p>
      )}
      {state.message && (
        <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-900" role="status">
          {state.message}
        </p>
      )}
      {state.status === "ready" && state.items.length === 0 && (
        <div className="mt-3 rounded-xl bg-[#f4f7f3] p-3">
          <p className="text-sm font-semibold text-[#344740]">No inquiries yet</p>
          <p className="mt-1 text-xs leading-5 text-[#60706a]">
            Messages from published contact forms will appear here.
          </p>
        </div>
      )}
      {state.items.length > 0 && (
        <ol className="mt-3 space-y-3">
          {state.items.map((inquiry) => (
            <li key={inquiry.inquiry_id} className="rounded-xl border border-[#dce4dc] p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#102a27]">{inquiry.name}</p>
                  <a className="break-all text-xs text-[#087f67] underline" href={`mailto:${inquiry.email}`}>
                    {inquiry.email}
                  </a>
                </div>
                <span className="rounded-full bg-[#edf3ed] px-2 py-1 text-[10px] font-bold uppercase text-[#52635d]">
                  {inquiry.status}
                </span>
              </div>
              {inquiry.phone && (
                <a className="mt-1 block text-xs text-[#52635d] underline" href={`tel:${inquiry.phone}`}>
                  {inquiry.phone}
                </a>
              )}
              <p className="mt-3 whitespace-pre-wrap text-xs leading-5 text-[#42534d]">{inquiry.message}</p>
              <p className="mt-2 text-[10px] uppercase tracking-wider text-[#7a8882]">
                {inquiry.source_page_title} · {new Date(inquiry.received_at).toLocaleString()}
              </p>
              <div className="mt-3 flex gap-2">
                {inquiry.status === "new" && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus(inquiry.inquiry_id, "read")}>Mark read</Button>
                )}
                {inquiry.status !== "resolved" && (
                  <Button size="sm" onClick={() => updateStatus(inquiry.inquiry_id, "resolved")}>Resolve</Button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
      {state.status === "error" && (
        <Button className="mt-3 w-full" size="sm" variant="outline" onClick={load}>Retry</Button>
      )}
    </details>
  );
};

const WebsiteAnalytics = ({ orgId, token }) => {
  const [state, setState] = useState({ status: "idle", data: null });
  const load = async () => {
    setState((current) => ({ ...current, status: "loading" }));
    try {
      const response = await axios.get(
        `${API}/organizations/${orgId}/website/analytics`,
        { headers: authHeaders(token), params: { days: 30 } },
      );
      setState({ status: "ready", data: response.data });
    } catch (_error) {
      setState({ status: "error", data: null });
    }
  };
  const data = state.data;
  return (
    <details
      className="mt-4 rounded-2xl border border-[#c8d4ca] bg-white p-3"
      onToggle={(event) => {
        if (event.currentTarget.open && state.status === "idle") load();
      }}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-[#344740]">
        <BarChart3 className="h-4 w-4 text-[#087f67]" />
        Website analytics
      </summary>
      <p className="mt-2 text-xs leading-5 text-[#60706a]">
        Privacy-conscious activity from the last 30 days.
      </p>
      {state.status === "loading" && (
        <p className="mt-3 text-sm text-[#60706a]" role="status">Loading analytics…</p>
      )}
      {state.status === "error" && (
        <div className="mt-3">
          <p className="text-xs text-amber-900" role="alert">Analytics could not be loaded.</p>
          <Button className="mt-2 w-full" size="sm" variant="outline" onClick={load}>Retry</Button>
        </div>
      )}
      {state.status === "ready" && data && (
        <div className="mt-3">
          <dl className="grid grid-cols-2 gap-2">
            {[
              ["Visits", data.visits],
              ["Page views", data.page_views],
              ["Registration clicks", data.registration_clicks],
              ["CTA clicks", data.cta_clicks],
              ["Inquiries", data.inquiries],
              ["Inquiry conversion", `${data.inquiry_conversion_rate}%`],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl bg-[#f4f7f3] p-2.5">
                <dt className="text-[10px] uppercase tracking-wider text-[#60706a]">{label}</dt>
                <dd className="mt-1 text-lg font-bold text-[#102a27]">{value}</dd>
              </div>
            ))}
          </dl>
          <h3 className="mt-4 text-xs font-bold uppercase tracking-wider text-[#60706a]">Top pages</h3>
          {data.top_pages.length ? (
            <ol className="mt-2 space-y-2">
              {data.top_pages.map((page) => (
                <li key={page.page_id} className="flex items-center justify-between gap-3 text-xs">
                  <span className="truncate text-[#344740]">{page.title}</span>
                  <strong className="text-[#102a27]">{page.views}</strong>
                </li>
              ))}
            </ol>
          ) : (
            <p className="mt-2 text-xs text-[#60706a]">Visits will appear after people view the published site.</p>
          )}
        </div>
      )}
    </details>
  );
};

const WebsiteDomainControls = ({ orgId, token }) => {
  const [state, setState] = useState({ status: "idle", data: null, message: "" });
  const [domain, setDomain] = useState("");
  const request = async (action) => {
    setState((current) => ({ ...current, status: "loading", message: "" }));
    try {
      const config = { headers: authHeaders(token) };
      const response = action === "connect"
        ? await axios.post(`${API}/organizations/${orgId}/website/domain`, { domain }, config)
        : action === "disconnect"
          ? await axios.delete(`${API}/organizations/${orgId}/website/domain`, config)
          : action === "verify"
            ? await axios.post(`${API}/organizations/${orgId}/website/domain/verify`, {}, config)
            : await axios.get(`${API}/organizations/${orgId}/website/domain`, config);
      setState({ status: "ready", data: response.data, message: "" });
      setDomain(response.data.custom_domain || "");
    } catch (error) {
      setState((current) => ({
        ...current,
        status: "error",
        message: error.response?.data?.detail || "Domain settings could not be updated.",
      }));
    }
  };
  const connected = state.data?.custom_domain;
  return (
    <details
      className="mt-4 rounded-2xl border border-[#c8d4ca] bg-white p-3"
      onToggle={(event) => {
        if (event.currentTarget.open && state.status === "idle") request("load");
      }}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-[#344740]">
        <Globe2 className="h-4 w-4 text-[#087f67]" />
        Website address
      </summary>
      {state.status === "loading" && !state.data && (
        <p className="mt-3 text-sm text-[#60706a]" role="status">Loading domain…</p>
      )}
      {state.data && (
        <div className="mt-3">
          <p className="text-xs text-[#60706a]">Included address</p>
          <p className="mt-1 break-all text-xs font-semibold text-[#344740]">{state.data.included_domain}</p>
          <label className="mt-4 block text-xs font-bold text-[#42534d]">
            Custom domain
            <input
              type="text"
              value={domain}
              disabled={Boolean(connected)}
              onChange={(event) => setDomain(event.target.value)}
              placeholder="www.yourclub.org"
              autoCapitalize="none"
              autoCorrect="off"
              className="mt-2 min-h-11 w-full rounded-xl border border-[#cbd6ce] px-3 text-sm disabled:bg-[#f4f7f3]"
            />
          </label>
          {connected ? (
            <>
              <p className="mt-3 text-xs">
                Status: <strong className="uppercase text-[#087f67]">{state.data.status}</strong>
              </p>
              {state.data.dns && (
                <div className="mt-3 rounded-xl bg-[#f4f7f3] p-3 text-xs leading-5 text-[#42534d]">
                  <p><strong>{state.data.dns.type}</strong></p>
                  <p>Name: <code>{state.data.dns.name}</code></p>
                  {state.data.dns.target && <p>Target: <code className="break-all">{state.data.dns.target}</code></p>}
                  {state.data.dns.guidance && <p>{state.data.dns.guidance}</p>}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => request("verify")}>Check connection</Button>
                <Button size="sm" variant="ghost" onClick={() => request("disconnect")}>Disconnect</Button>
              </div>
            </>
          ) : (
            <Button className="mt-3 w-full" size="sm" disabled={!domain.trim()} onClick={() => request("connect")}>Connect domain</Button>
          )}
        </div>
      )}
      {state.message && (
        <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-900" role="alert">{state.message}</p>
      )}
    </details>
  );
};

const WebsiteOperationalHealth = ({ orgId, token, refreshKey }) => {
  const [state, setState] = useState({ status: "idle", data: null, message: "" });
  const load = useCallback(async () => {
    setState((current) => ({ ...current, status: "loading", message: "" }));
    try {
      const response = await axios.get(
        `${API}/organizations/${orgId}/website/health`,
        { headers: authHeaders(token) },
      );
      setState({ status: "ready", data: response.data, message: "" });
    } catch (_error) {
      setState((current) => ({
        ...current,
        status: "error",
        message: "Website health could not be checked. Your live website is unchanged.",
      }));
    }
  }, [orgId, token]);
  const updateNotifications = async (enabled) => {
    try {
      await axios.patch(
        `${API}/organizations/${orgId}/website/health/preferences`,
        { enabled },
        { headers: authHeaders(token) },
      );
      setState((current) => ({
        ...current,
        data: { ...current.data, notifications_enabled: enabled },
        message: "",
      }));
    } catch (_error) {
      setState((current) => ({
        ...current,
        message: "Health notification preferences could not be saved.",
      }));
    }
  };

  useEffect(() => {
    if (state.status !== "idle") load();
    // A publish or unpublish result should refresh an already-open report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const tone = state.data?.status === "healthy"
    ? "bg-emerald-50 text-emerald-800"
    : state.data?.status === "offline"
      ? "bg-slate-100 text-slate-700"
      : "bg-amber-50 text-amber-900";
  const label = state.data?.status === "healthy"
    ? "Healthy"
    : state.data?.status === "offline"
      ? "Offline"
      : "Needs attention";

  return (
    <details
      className="mt-4 rounded-2xl border border-[#c8d4ca] bg-white p-3"
      onToggle={(event) => {
        if (event.currentTarget.open && state.status === "idle") load();
      }}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-sm font-semibold text-[#344740]">
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#087f67]" />
          Website health
        </span>
        {state.data && (
          <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${tone}`}>
            {label}
          </span>
        )}
      </summary>
      {state.status === "loading" && !state.data && (
        <p className="mt-3 text-xs text-[#60706a]" role="status">Checking website health…</p>
      )}
      {state.data && (
        <div className="mt-3">
          <p className="text-xs leading-5 text-[#60706a]">
            {state.data.summary.passed} passed · {state.data.summary.warnings} warnings · {state.data.summary.failed} failed
          </p>
          {state.data.live_url && (
            <a className="mt-1 block break-all text-xs font-semibold text-[#087f67] underline" href={state.data.live_url} target="_blank" rel="noreferrer">
              {state.data.live_url}
            </a>
          )}
          <ul className="mt-3 space-y-2">
            {state.data.checks.map((check) => (
              <li key={check.key} className="rounded-xl border border-[#dce4dc] p-3 text-xs">
                <div className="flex items-start gap-2">
                  {check.status === "pass" ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-label="Passed" />
                  ) : (
                    <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${check.status === "fail" ? "text-red-600" : "text-amber-600"}`} aria-label={check.status === "fail" ? "Failed" : "Warning"} />
                  )}
                  <div>
                    <strong className="text-[#102a27]">{check.label}</strong>
                    <p className="mt-1 leading-5 text-[#60706a]">{check.detail}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {state.data.incidents?.length > 0 && (
            <div className="mt-4 border-t border-[#dce4dc] pt-3">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#60706a]">Recent status changes</h3>
              <ol className="mt-2 space-y-2">
                {state.data.incidents.slice(0, 5).map((incident) => (
                  <li key={incident.incident_id} className="rounded-xl bg-[#f4f7f3] p-3 text-xs text-[#42534d]">
                    <strong className="capitalize text-[#102a27]">{incident.from_status.replace("_", " ")} → {incident.to_status.replace("_", " ")}</strong>
                    <time className="mt-1 block text-[#60706a]" dateTime={incident.observed_at}>
                      {new Date(incident.observed_at).toLocaleString()}
                    </time>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <label className="mt-4 flex min-h-11 items-start gap-3 rounded-xl border border-[#dce4dc] p-3 text-xs text-[#42534d]">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-[#087f67]"
              checked={state.data.notifications_enabled !== false}
              onChange={(event) => updateNotifications(event.target.checked)}
            />
            <span>
              <strong className="block text-[#102a27]">Email organization admins</strong>
              Send one alert when health degrades and one recovery notice when it returns healthy.
            </span>
          </label>
          <Button className="mt-3 w-full" size="sm" variant="outline" onClick={load} disabled={state.status === "loading"}>
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${state.status === "loading" ? "animate-spin" : ""}`} />
            Check again
          </Button>
        </div>
      )}
      {state.message && <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-900" role="alert">{state.message}</p>}
      {state.status === "error" && <Button className="mt-2 w-full" size="sm" variant="outline" onClick={load}>Retry</Button>}
    </details>
  );
};

const WebsiteReleaseHistory = ({ orgId, token, site, onPreview, onRestored }) => {
  const [state, setState] = useState({ status: "idle", items: [], message: "" });
  const [confirmId, setConfirmId] = useState(null);
  const load = async () => {
    setState((current) => ({ ...current, status: "loading", message: "" }));
    try {
      const response = await axios.get(`${API}/organizations/${orgId}/website/releases`, {
        headers: authHeaders(token),
      });
      setState({ status: "ready", items: response.data.items || [], message: "" });
    } catch (_error) {
      setState((current) => ({ ...current, status: "error", message: "Release history could not be loaded." }));
    }
  };
  const preview = async (releaseId) => {
    try {
      const response = await axios.get(
        `${API}/organizations/${orgId}/website/releases/${releaseId}`,
        { headers: authHeaders(token) },
      );
      onPreview(response.data);
    } catch (_error) {
      setState((current) => ({ ...current, message: "That version could not be previewed." }));
    }
  };
  const restore = async (releaseId) => {
    try {
      const response = await axios.post(
        `${API}/organizations/${orgId}/website/releases/${releaseId}/restore`,
        { expected_revision: site.revision },
        { headers: authHeaders(token) },
      );
      setConfirmId(null);
      onRestored(response.data.site);
      await load();
    } catch (error) {
      setState((current) => ({
        ...current,
        message: error.response?.status === 409
          ? "The site changed before this restore. Reload and try again."
          : "This version could not be restored.",
      }));
    }
  };
  return (
    <details
      className="mt-4 rounded-2xl border border-[#c8d4ca] bg-white p-3"
      onToggle={(event) => {
        if (event.currentTarget.open && state.status === "idle") load();
      }}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-[#344740]">
        <HistoryIcon className="h-4 w-4 text-[#087f67]" />
        Release history
      </summary>
      {state.status === "loading" && <p className="mt-3 text-sm text-[#60706a]" role="status">Loading releases…</p>}
      {state.items.length > 0 && (
        <ol className="mt-3 space-y-2">
          {state.items.map((release) => (
            <li key={release.release_id} className="rounded-xl border border-[#dce4dc] p-3 text-xs">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-bold text-[#102a27]">Version {release.sequence}</p>
                  <p className="mt-1 text-[#60706a]">{new Date(release.created_at).toLocaleString()}</p>
                  <p className="mt-1 text-[#60706a]">By {release.created_by_name} · {release.page_count} {release.page_count === 1 ? "page" : "pages"}</p>
                </div>
                {release.is_current && <span className="rounded-full bg-[#edf3ed] px-2 py-1 text-[10px] font-bold uppercase">Live</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => preview(release.release_id)}>Preview</Button>
                {!release.is_current && confirmId !== release.release_id && (
                  <Button size="sm" variant="ghost" onClick={() => setConfirmId(release.release_id)}>Restore</Button>
                )}
              </div>
              {confirmId === release.release_id && (
                <div className="mt-3 rounded-lg bg-amber-50 p-2 text-amber-900">
                  <p>Make this version live? Your saved draft will not be changed.</p>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" onClick={() => restore(release.release_id)}><RotateCcw className="mr-1 h-3.5 w-3.5" />Restore live</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      {state.status === "ready" && state.items.length === 0 && <p className="mt-3 text-xs text-[#60706a]">Published versions will appear here.</p>}
      {state.message && <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-900" role="alert">{state.message}</p>}
      {state.status === "error" && <Button className="mt-2 w-full" size="sm" variant="outline" onClick={load}>Retry</Button>}
    </details>
  );
};

const scheduledTime = (value, timezoneName = "UTC") => new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: timezoneName,
}).format(new Date(value));

const SavedSectionCard = ({ item, onAdd, onRename, onDelete }) => {
  const [name, setName] = useState(item.name);
  return (
    <div className="rounded-xl border border-[#b9d8ca] bg-[#eff9f2] p-3">
      <label className="block text-xs font-bold text-[#42534d]">
        Section name
        <input
          value={name}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
          className="mt-1 min-h-10 w-full rounded-lg border border-[#b9c9bd] bg-white px-3 text-sm"
        />
      </label>
      <p className="mt-1 text-xs text-[#60706a]">{BLOCK_LABELS[item.type] || item.type}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => onAdd(item)}>Add to page</Button>
        <Button size="sm" variant="outline" disabled={!name.trim() || name.trim() === item.name} onClick={() => onRename(item, name.trim())}>Save name</Button>
        <Button size="sm" variant="outline" onClick={() => onDelete(item)}>Delete</Button>
      </div>
    </div>
  );
};

const WebsitePublishingSchedule = ({ orgId, token, site, disabled }) => {
  const [state, setState] = useState({ status: "idle", items: [], message: "", timezoneName: "UTC" });
  const [executeAt, setExecuteAt] = useState("");
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [action, setAction] = useState("publish");
  const load = async () => {
    setState((current) => ({ ...current, status: "loading", message: "" }));
    try {
      const response = await axios.get(`${API}/organizations/${orgId}/website/schedules`, {
        headers: authHeaders(token),
      });
      setState({ status: "ready", items: response.data.items || [], message: "", timezoneName: response.data.timezone_name || "UTC" });
    } catch (_error) {
      setState((current) => ({ ...current, status: "error", message: "Publishing schedules could not be loaded." }));
    }
  };
  const create = async (event) => {
    event.preventDefault();
    setState((current) => ({ ...current, status: "saving", message: "" }));
    try {
      const response = await axios.post(
        `${API}/organizations/${orgId}/website/schedules`,
        {
          action,
          execute_at_local: executeAt,
          expected_revision: site.revision,
          requires_approval: requiresApproval,
        },
        { headers: authHeaders(token) },
      );
      setState((current) => ({ ...current, status: "ready", items: [response.data, ...current.items], message: "" }));
      setExecuteAt("");
    } catch (error) {
      setState((current) => ({
        ...current,
        status: "error",
        message: error.response?.status === 409
          ? "The site changed or already has an active schedule. Reload and try again."
          : "The publishing schedule could not be created.",
      }));
    }
  };
  const act = async (scheduleId, operation) => {
    try {
      const url = `${API}/organizations/${orgId}/website/schedules/${scheduleId}`;
      const response = operation === "approve"
        ? await axios.post(`${url}/approve`, {}, { headers: authHeaders(token) })
        : await axios.delete(url, { headers: authHeaders(token) });
      setState((current) => ({
        ...current,
        status: "ready",
        message: "",
        items: current.items.map((item) => item.schedule_id === scheduleId ? response.data : item),
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        message: error.response?.data?.detail?.code === "second_admin_required"
          ? "A different organization admin must approve this schedule."
          : "The schedule could not be updated.",
      }));
    }
  };
  const active = state.items.find((item) => ["awaiting_approval", "scheduled", "processing"].includes(item.status));
  return (
    <details
      className="mt-4 rounded-2xl border border-[#c8d4ca] bg-white p-3"
      onToggle={(event) => {
        if (event.currentTarget.open && state.status === "idle") load();
      }}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-[#344740]">
        <HistoryIcon className="h-4 w-4 text-[#087f67]" />
        Publishing schedule
      </summary>
      {state.status === "loading" && <p className="mt-3 text-xs text-[#60706a]" role="status">Loading schedule…</p>}
      {state.status !== "idle" && !active && (
        <form className="mt-3 space-y-3" onSubmit={create}>
          <label className="block text-xs font-bold text-[#42534d]">
            Action
            <select value={action} onChange={(event) => setAction(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border border-[#cbd6ce] bg-white px-3 text-sm">
              <option value="publish">Publish saved draft</option>
              {site?.current_release_id && <option value="unpublish">Take website offline</option>}
            </select>
          </label>
          <label className="block text-xs font-bold text-[#42534d]">
            Date and time
            <input type="datetime-local" value={executeAt} onChange={(event) => setExecuteAt(event.target.value)} required className="mt-1 min-h-11 w-full rounded-xl border border-[#cbd6ce] px-3 text-sm" />
            <span className="mt-1 block font-normal text-[#60706a]">{state.timezoneName} organization time</span>
          </label>
          <label className="flex items-start gap-2 text-xs leading-5 text-[#42534d]">
            <input type="checkbox" checked={requiresApproval} onChange={(event) => setRequiresApproval(event.target.checked)} className="mt-1 h-4 w-4 accent-[#087f67]" />
            Require approval from a different organization admin
          </label>
          {disabled && <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900">Save the open section before scheduling this revision.</p>}
          <Button type="submit" size="sm" className="w-full" disabled={!executeAt || state.status === "saving" || disabled}>
            {state.status === "saving" ? "Scheduling…" : "Schedule action"}
          </Button>
        </form>
      )}
      {active && (
        <div className="mt-3 rounded-xl bg-[#f4f7f3] p-3 text-xs text-[#42534d]">
          <div className="flex items-center justify-between gap-2">
            <strong className="capitalize text-[#102a27]">{active.action}</strong>
            <span className="rounded-full bg-white px-2 py-1 text-[10px] font-bold uppercase">{active.status.replace("_", " ")}</span>
          </div>
          <time className="mt-2 block" dateTime={active.execute_at}>{scheduledTime(active.execute_at, active.timezone_name)} · {active.timezone_name}</time>
          <p className="mt-1 text-[#60706a]">Version revision {active.expected_revision} · by {active.created_by_name}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {active.status === "awaiting_approval" && <Button size="sm" onClick={() => act(active.schedule_id, "approve")}>Approve</Button>}
            {active.status !== "processing" && <Button size="sm" variant="outline" onClick={() => act(active.schedule_id, "cancel")}>Cancel</Button>}
          </div>
        </div>
      )}
      {state.items.filter((item) => !["awaiting_approval", "scheduled", "processing"].includes(item.status)).slice(0, 3).map((item) => (
        <p key={item.schedule_id} className="mt-2 text-[11px] text-[#60706a]">
          <span className="capitalize">{item.action}</span> · {item.status.replace("_", " ")} · {scheduledTime(item.execute_at, item.timezone_name)}
        </p>
      ))}
      {state.message && <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-900" role="alert">{state.message}</p>}
    </details>
  );
};

const PageSettings = ({
  page,
  site,
  index,
  total,
  onSave,
  onMove,
  onDuplicate,
  onDelete,
  forceOpen = false,
}) => {
  const [values, setValues] = useState({
    title: page.title,
    route_slug: page.route_slug,
    status: page.status || "draft",
    nav_visible: page.nav_visible !== false,
    seo_title: page.seo?.title || "",
    seo_description: page.seo?.description || "",
    social_image_url: page.seo?.social_image_url || "",
  });
  useEffect(() => {
    setValues({
      title: page.title,
      route_slug: page.route_slug,
      status: page.status || "draft",
      nav_visible: page.nav_visible !== false,
      seo_title: page.seo?.title || "",
      seo_description: page.seo?.description || "",
      social_image_url: page.seo?.social_image_url || "",
    });
  }, [page]);
  const update = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));
  const publishedAt = Date.parse(site?.published_at || "");
  const pageStatus = values.status === "hidden"
    ? "Hidden"
    : site?.current_release_id &&
        Number.isFinite(publishedAt) &&
        Date.parse(page.updated_at || "") <= publishedAt
      ? "Published"
      : "Draft";
  return (
    <details
      open={forceOpen || undefined}
      className="mt-4 rounded-2xl border border-[#c8d4ca] bg-white p-3"
    >
      <summary className="cursor-pointer list-none text-sm font-semibold text-[#344740]">
        Page settings
        <span className="float-right rounded-full bg-[#edf3ed] px-2 py-1 text-[10px] uppercase tracking-wider text-[#60706a]">
          {pageStatus}
        </span>
      </summary>
      <form
        className="mt-4 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSave({
            title: values.title.trim(),
            route_slug: values.route_slug.trim(),
            status: values.status,
            nav_visible: values.nav_visible,
            seo: {
              title: values.seo_title.trim(),
              description: values.seo_description.trim(),
              social_image_url: values.social_image_url.trim(),
            },
          });
        }}
      >
        <label className="block text-xs font-bold text-[#42534d]">
          Page name
          <input
            value={values.title}
            onChange={(event) => update("title", event.target.value)}
            maxLength={160}
            required
            className="mt-1 min-h-11 w-full rounded-xl border border-[#cbd6ce] px-3 text-sm"
          />
        </label>
        <label className="block text-xs font-bold text-[#42534d]">
          URL slug
          <span className="mt-1 flex items-center rounded-xl border border-[#cbd6ce] bg-white pl-3 text-sm text-[#60706a]">
            /
            <input
              aria-label="URL slug"
              value={values.route_slug}
              onChange={(event) =>
                update(
                  "route_slug",
                  event.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, "-"),
                )
              }
              required
              className="min-h-11 min-w-0 flex-1 rounded-xl px-1 text-[#102a27] outline-none"
            />
          </span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs font-bold text-[#42534d]">
            Page visibility
            <select
              value={values.status}
              onChange={(event) => update("status", event.target.value)}
              className="mt-1 min-h-11 w-full rounded-xl border border-[#cbd6ce] px-2 text-sm"
            >
              <option value="draft">Visible when published</option>
              <option value="hidden">Hidden from live site</option>
            </select>
          </label>
          <label className="flex min-h-11 items-center gap-2 self-end rounded-xl border border-[#cbd6ce] px-3 text-xs font-bold text-[#42534d]">
            <input
              type="checkbox"
              checked={values.nav_visible}
              onChange={(event) => update("nav_visible", event.target.checked)}
            />
            Show in navigation
          </label>
        </div>
        <fieldset className="space-y-3 rounded-xl bg-[#f4f7f3] p-3">
          <legend className="px-1 text-xs font-bold text-[#42534d]">Search and sharing</legend>
          <input
            aria-label="SEO title"
            placeholder="SEO title"
            value={values.seo_title}
            onChange={(event) => update("seo_title", event.target.value)}
            maxLength={160}
            className="min-h-11 w-full rounded-xl border border-[#cbd6ce] px-3 text-sm"
          />
          <textarea
            aria-label="SEO description"
            placeholder="SEO description"
            value={values.seo_description}
            onChange={(event) => update("seo_description", event.target.value)}
            maxLength={320}
            className="min-h-20 w-full rounded-xl border border-[#cbd6ce] p-3 text-sm"
          />
          <input
            aria-label="Social image URL"
            placeholder="https://…"
            type="url"
            value={values.social_image_url}
            onChange={(event) => update("social_image_url", event.target.value)}
            className="min-h-11 w-full rounded-xl border border-[#cbd6ce] px-3 text-sm"
          />
        </fieldset>
        <Button type="submit" className="w-full">Save page settings</Button>
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" disabled={index === 0} onClick={() => onMove(-1)}>
            <ArrowUp className="mr-2 h-4 w-4" /> Move up
          </Button>
          <Button type="button" variant="outline" disabled={index === total - 1} onClick={() => onMove(1)}>
            <ArrowDown className="mr-2 h-4 w-4" /> Move down
          </Button>
          <Button type="button" variant="outline" onClick={onDuplicate}>
            <Copy className="mr-2 h-4 w-4" /> Duplicate page
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={page.stable_key === "home"}
            onClick={onDelete}
            className="text-[#9b3e2f]"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete page
          </Button>
        </div>
      </form>
    </details>
  );
};

const assetIdsIn = (value) => {
  if (Array.isArray(value)) return value.flatMap(assetIdsIn);
  if (!value || typeof value !== "object") return [];
  return Object.entries(value).flatMap(([key, nested]) =>
    ["image_asset_id", "logo_asset_id"].includes(key) && nested
      ? [nested]
      : assetIdsIn(nested),
  );
};

const ProgramCard = ({ program }) => (
  <article
    className="rounded-2xl border border-black/15 bg-white p-4"
    data-state={program.availability?.state}
  >
    <div className="flex items-start gap-3">
      {program.logo_url ? (
        <img
          src={program.logo_url}
          alt=""
          className="h-10 w-10 rounded-lg object-cover"
        />
      ) : (
        <div
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-black text-xs font-black text-[#c8ff45]"
          aria-hidden="true"
        >
          {program.logo_fallback?.initials || "PRG"}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <h4 className="break-words font-bold leading-tight">{program.name}</h4>
        <p className="mt-1 text-xs uppercase tracking-wider text-black/55">
          {program.availability?.state?.replaceAll("_", " ")}
        </p>
      </div>
    </div>
    <div className="mt-4 flex items-center justify-between gap-3 text-sm">
      <span>{program.price?.display || "Price unavailable"}</span>
      {program.availability?.cta !== "none" ? (
        <span className="rounded-full bg-black px-3 py-1.5 font-bold text-[#c8ff45]">
          {program.availability.cta === "join_waitlist"
            ? "Join waitlist"
            : "Register"}
        </span>
      ) : (
        <span className="text-black/50">Unavailable</span>
      )}
    </div>
  </article>
);

const ClubhousePreview = ({
  page,
  theme,
  programs,
  viewport,
  selectedBlockId,
  onSelectBlock,
}) => {
  const blocks = page?.blocks || [];
  const isMobile = viewport === "mobile";
  const isTablet = viewport === "tablet";
  const twoColumnGrid = isMobile ? "grid-cols-1" : "grid-cols-2";
  return (
    <div
      style={themeStyle(theme)}
      className={`mx-auto overflow-hidden rounded-[26px] border border-black/20 shadow-2xl transition-all [&_h1]:font-[var(--website-heading-font)] [&_h2]:font-[var(--website-heading-font)] [&_h3]:font-[var(--website-heading-font)] ${isMobile ? "w-full max-w-[390px]" : isTablet ? "w-[768px] min-w-[768px]" : "w-[960px] min-w-[960px]"}`}
      aria-label={`${viewport} private preview`}
      data-preview-mode={viewport}
    >
      <header className="flex items-center justify-between border-b border-black bg-black px-5 py-4 text-white">
        <span className="text-sm font-black uppercase tracking-[0.18em]">
          Clubhouse
        </span>
        {isMobile ? (
          <span className="rounded-lg border border-white/30 px-3 py-2 text-xs font-bold">
            ☰ Menu
          </span>
        ) : (
          <span className="text-xs text-white/60">Private preview</span>
        )}
      </header>
      <main style={{ ...themeStyle(theme), ...appearanceStyle(page?.appearance) }}>
        {blocks.length === 0 ? (
          <div className="p-12 text-center text-black/55">
            Add a block to begin your page.
          </div>
        ) : (
          blocks
            .filter(
              (block) =>
                block.visibility !== "hidden" &&
                !(
                  isMobile &&
                  block.appearance?.mobile_visibility === "hidden"
                ),
            )
            .map((block, index) => {
              const sectionPadding = previewSectionPadding(
                block.appearance,
                viewport,
              );
              const content = (() => {
              if (block.type === "hero")
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`bg-[var(--website-primary)] text-[var(--website-primary-text)] ${isMobile ? previewSectionPadding(block.appearance, viewport) : isTablet ? "px-8 py-16" : "px-10 py-20"}`}
                  >
                    {block.props.image_url && (
                      <img
                        src={block.props.image_url}
                        alt={
                          block.props.image_decorative
                            ? ""
                            : block.props.image_alt
                        }
                        className={`mb-8 w-full rounded-2xl object-cover ${isMobile ? "aspect-[4/3]" : "aspect-[16/7]"}`}
                      />
                    )}
                    <div className="text-xs font-black uppercase tracking-[.22em] text-[var(--website-accent)]">
                      01 / Welcome
                    </div>
                    <h2
                      className={`mt-5 max-w-4xl break-words font-black uppercase leading-[.92] ${isMobile ? "text-4xl" : "text-7xl"}`}
                    >
                      {block.props.headline}
                    </h2>
                    <p className="mt-5 max-w-2xl text-white/70">
                      {block.props.subheadline}
                    </p>
                  </section>
                );
              if (block.type === "navigation")
                return (
                  <nav
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`${isMobile ? "hidden" : "flex"} flex-wrap gap-4 border-b border-black/20 px-6 py-4`}
                    aria-label="Preview navigation"
                  >
                    {block.props.items.map((item) => (
                      <span
                        key={`${item.label}-${item.url}`}
                        className="font-bold"
                      >
                        {item.label}
                      </span>
                    ))}
                  </nav>
                );
              if (block.type === "image_text")
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`grid gap-6 border-t border-black/20 ${sectionPadding} ${twoColumnGrid}`}
                  >
                    <img
                      src={block.props.image_url}
                      alt={block.props.alt}
                      className="h-full min-h-48 w-full rounded-2xl object-cover"
                    />
                    <div>
                      <h3 className="text-3xl font-black uppercase">
                        {block.props.heading}
                      </h3>
                      <p className="mt-3 whitespace-pre-wrap text-black/70">
                        {block.props.body}
                      </p>
                    </div>
                  </section>
                );
              if (block.type === "rich_text")
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`border-t border-black/20 ${sectionPadding}`}
                  >
                    <p className="whitespace-pre-wrap text-black/70">
                      {block.props.content}
                    </p>
                  </section>
                );
              if (
                block.type === "program_grid" ||
                block.type === "featured_program" ||
                block.type === "upcoming_events" ||
                block.type === "registration_cta"
              ) {
                const selectedIds =
                  block.type === "featured_program" ||
                  block.type === "registration_cta"
                    ? [block.props.program_id]
                    : block.props.program_ids || [];
                const selectedPrograms = selectedIds.map((id) =>
                  programs.find((program) => program.program_id === id),
                );
                const missingIds = selectedIds.filter(
                  (id, selectedIndex) => id && !selectedPrograms[selectedIndex],
                );
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`border-t border-black/20 ${sectionPadding}`}
                  >
                    <div className="mb-5 flex items-end justify-between">
                      <div>
                        <span className="text-xs font-black uppercase tracking-[.2em]">
                          {String(index + 1).padStart(2, "0")} / Programs
                        </span>
                        <h3 className="mt-2 text-3xl font-black uppercase">
                          {block.type === "registration_cta"
                            ? block.props.label
                            : block.props.heading || "Programs"}
                        </h3>
                      </div>
                    </div>
                    {missingIds.length > 0 && (
                      <div className="mb-3 rounded-xl border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
                        Selected program is no longer available:{" "}
                        {missingIds.join(", ")}
                      </div>
                    )}
                    {selectedPrograms.filter(Boolean).length ? (
                      <div
                        className={`grid gap-3 ${twoColumnGrid}`}
                        data-testid="program-preview-grid"
                        data-preview-layout={
                          isMobile ? "single-column" : "two-column"
                        }
                      >
                        {selectedPrograms.filter(Boolean).map((program) => (
                          <ProgramCard
                            key={program.program_id}
                            program={program}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-black/30 p-8 text-center">
                        {selectedIds.length
                          ? "Selected program is unavailable."
                          : "Choose a program for this block."}
                      </div>
                    )}
                  </section>
                );
              }
              if (block.type === "cta")
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`bg-[#c8ff45] ${previewSectionPadding(block.appearance, viewport)}`}
                  >
                    <h3 className="text-3xl font-black uppercase">
                      {block.props.heading}
                    </h3>
                    <span className="mt-5 inline-block rounded-full bg-black px-5 py-3 font-bold text-white">
                      {block.props.label}
                    </span>
                  </section>
                );
              if (block.type === "staff")
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`border-t border-black/20 ${sectionPadding}`}
                  >
                    <h3 className="text-2xl font-black uppercase">
                      {block.props.heading || "Staff"}
                    </h3>
                    <div className={`mt-5 grid gap-3 ${twoColumnGrid}`}>
                      {block.props.members.map((member) => (
                        <article
                          key={`${member.name}-${member.role}`}
                          className="rounded-2xl border bg-white p-4"
                        >
                          {member.image_url && (
                            <img
                              src={member.image_url}
                              alt={
                                member.image_decorative
                                  ? ""
                                  : member.image_alt || ""
                              }
                              className="mb-3 h-20 w-20 rounded-full object-cover"
                            />
                          )}
                          <h4 className="font-bold">{member.name}</h4>
                          {member.role && (
                            <p className="text-sm">{member.role}</p>
                          )}
                          {member.bio && (
                            <p className="mt-2 text-sm text-black/65">
                              {member.bio}
                            </p>
                          )}
                        </article>
                      ))}
                    </div>
                  </section>
                );
              if (block.type === "sponsors")
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`border-t border-black/20 ${sectionPadding}`}
                  >
                    <h3 className="text-2xl font-black uppercase">
                      {block.props.heading || "Sponsors"}
                    </h3>
                    <div className="mt-5 flex flex-wrap gap-4">
                      {block.props.items.map((item) => (
                        <article
                          key={item.name}
                          className="rounded-xl border bg-white p-4"
                        >
                          <img
                            src={item.logo_url}
                            alt={
                              item.logo_decorative
                                ? ""
                                : item.logo_alt || `${item.name} logo`
                            }
                            className="h-14 w-28 object-contain"
                          />
                          <p className="mt-2 text-center text-sm font-bold">
                            {item.name}
                          </p>
                        </article>
                      ))}
                    </div>
                  </section>
                );
              if (block.type === "testimonials")
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`border-t border-black/20 ${sectionPadding}`}
                  >
                    <h3 className="text-2xl font-black uppercase">
                      {block.props.heading || "Testimonials"}
                    </h3>
                    <div className={`mt-5 grid gap-3 ${twoColumnGrid}`}>
                      {block.props.items.map((item) => (
                        <blockquote
                          key={`${item.quote}-${item.attribution}`}
                          className="rounded-2xl bg-white p-5"
                        >
                          <p>“{item.quote}”</p>
                          <footer className="mt-3 text-sm font-bold">
                            — {item.attribution}
                          </footer>
                        </blockquote>
                      ))}
                    </div>
                  </section>
                );
              if (block.type === "faq")
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`border-t border-black/20 ${sectionPadding}`}
                  >
                    <h3 className="text-2xl font-black uppercase">
                      {block.props.heading || "FAQ"}
                    </h3>
                    <dl className="mt-5 space-y-4">
                      {block.props.items.map((item) => (
                        <div
                          key={item.question}
                          className="rounded-xl border bg-white p-4"
                        >
                          <dt className="font-bold">{item.question}</dt>
                          <dd className="mt-2 text-black/70">{item.answer}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                );
              if (block.type === "contact_form")
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`border-t border-black/20 ${sectionPadding}`}
                  >
                    <h3 className="text-2xl font-black uppercase">
                      {block.props.heading || "Contact us"}
                    </h3>
                    <p className="mt-2 text-black/65">
                      Messages go to{" "}
                      {block.props.recipient_label || "organization staff"}.
                    </p>
                    <div className="mt-4 grid gap-3">
                      <span className="rounded-lg border bg-white p-3">
                        Name
                      </span>
                      <span className="rounded-lg border bg-white p-3">
                        Email
                      </span>
                      <span className="min-h-24 rounded-lg border bg-white p-3">
                        Message
                      </span>
                    </div>
                    {block.props.consent_text && (
                      <p className="mt-3 text-xs">{block.props.consent_text}</p>
                    )}
                  </section>
                );
              if (block.type === "location_contact")
                return (
                  <section
                    key={block.block_id}
                    style={previewAppearanceStyle(block.appearance, viewport)}
                    className={`border-t border-black/20 ${sectionPadding}`}
                  >
                    <h3 className="text-2xl font-black uppercase">
                      {block.props.heading || "Location and contact"}
                    </h3>
                    {block.props.body && (
                      <p className="mt-3 whitespace-pre-wrap text-black/70">
                        {block.props.body}
                      </p>
                    )}
                    <p className="mt-3 text-sm font-bold">
                      {block.props.show_public_address
                        ? "Approved public address will appear here."
                        : "Address is hidden."}
                    </p>
                  </section>
                );
              if (block.type === "footer")
                return (
                  <footer
                    key={block.block_id}
                    style={appearanceStyle(block.appearance)}
                    className={`border-t border-black bg-black text-white ${sectionPadding}`}
                  >
                    <p>{block.props.text}</p>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#c8ff45]">
                      {block.props.links.map((link) => (
                        <span key={`${link.label}-${link.url}`}>
                          {link.label}
                        </span>
                      ))}
                    </div>
                  </footer>
                );
              return (
                <section
                  key={block.block_id}
                  style={appearanceStyle(block.appearance)}
                  className={`border-t border-black/20 ${sectionPadding}`}
                >
                  <h3 className="text-2xl font-black uppercase">
                    {block.props.heading ||
                      BLOCK_LABELS[block.type] ||
                      block.type}
                  </h3>
                  <p className="mt-3 whitespace-pre-wrap text-black/70">
                    {block.props.content || "Content is ready to edit."}
                  </p>
                </section>
              );
              })();
              const label = BLOCK_LABELS[block.type] || block.type;
              return (
                <div
                  key={block.block_id}
                  className={`group relative cursor-pointer outline-none transition ${selectedBlockId === block.block_id ? "z-10 ring-4 ring-inset ring-[#087f67]" : "hover:ring-2 hover:ring-inset hover:ring-[#087f67]/60"}`}
                  role="button"
                  tabIndex={0}
                  aria-label={`Edit ${label} section`}
                  aria-pressed={selectedBlockId === block.block_id}
                  data-preview-block-id={block.block_id}
                  onClick={() => onSelectBlock?.(block.block_id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectBlock?.(block.block_id);
                    }
                  }}
                >
                  <span
                    className={`pointer-events-none absolute right-3 top-3 z-20 rounded-full bg-[#102a27] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg transition ${selectedBlockId === block.block_id ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus:opacity-100"}`}
                  >
                    {selectedBlockId === block.block_id
                      ? "Editing"
                      : `Edit ${label}`}
                  </span>
                  {content}
                </div>
              );
            })
        )}
      </main>
    </div>
  );
};

const AdminWebsitePro = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedAdminLayout : AdminLayout;
  const { token, user } = useAuth();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [site, setSite] = useState(null);
  const [previewTheme, setPreviewTheme] = useState(DEFAULT_THEME);
  const [pages, setPages] = useState([]);
  const [activePageId, setActivePageId] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [programError, setProgramError] = useState("");
  const [viewport, setViewport] = useState("desktop");
  const [mobilePanel, setMobilePanel] = useState("edit");
  const [isDirty, setIsDirty] = useState(false);
  const [activeUploads, setActiveUploads] = useState(0);
  const [pendingAssetIds, setPendingAssetIds] = useState([]);
  const hasUnsavedChanges = isDirty || activeUploads > 0;
  const [exitOpen, setExitOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [saveThenContinue, setSaveThenContinue] = useState(false);
  const [saveState, setSaveState] = useState("saved");
  const [editing, setEditing] = useState(null);
  const [fieldError, setFieldError] = useState("");
  const [addingPage, setAddingPage] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [savedSections, setSavedSections] = useState([]);
  const [savingSectionId, setSavingSectionId] = useState(null);
  const [savedSectionName, setSavedSectionName] = useState("");
  const [newPageTitle, setNewPageTitle] = useState("");
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [pageSettingsOpenId, setPageSettingsOpenId] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [historyPreview, setHistoryPreview] = useState(null);
  const [activeEditors, setActiveEditors] = useState([]);
  const editorSessionId = useRef(
    window.crypto?.randomUUID?.().replaceAll("-", "") ||
      `${Date.now()}_${Math.random().toString(36).slice(2)}`,
  );
  const lastControl = useRef(null);
  const closeButton = useRef(null);
  const dialogTrigger = useRef(null);
  const entryRoute = useRef(
    (() => {
      const stateRoute = window.history.state?.usr?.returnTo;
      if (stateRoute && stateRoute !== window.location.pathname)
        return stateRoute;
      try {
        const referrer = new URL(document.referrer);
        if (
          referrer.origin === window.location.origin &&
          referrer.pathname !== window.location.pathname
        ) {
          return `${referrer.pathname}${referrer.search}`;
        }
      } catch (_invalidReferrer) {
        // Fall through to the stable Website Pro entry point.
      }
      return "/admin/marketplace";
    })(),
  );
  const historyGuardActive = useRef(false);

  useEffect(() => {
    const preventUnload = (event) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    window.history.pushState(
      { websiteEditorGuard: true },
      "",
      window.location.href,
    );
    historyGuardActive.current = true;
    const preventBack = () => {
      window.history.pushState(
        { websiteEditorGuard: true },
        "",
        window.location.href,
      );
      historyGuardActive.current = true;
      dialogTrigger.current = document.activeElement;
      setPendingAction({ type: "exit" });
      setExitOpen(true);
    };
    window.addEventListener("popstate", preventBack);
    return () => {
      window.removeEventListener("popstate", preventBack);
      if (
        historyGuardActive.current &&
        window.history.state?.websiteEditorGuard
      ) {
        historyGuardActive.current = false;
        window.history.back();
      }
    };
  }, [hasUnsavedChanges]);

  const leaveEditor = () => {
    setIsDirty(false);
    window.location.assign(entryRoute.current);
  };

  const performAction = (action) => {
    if (!action || action.type === "exit") return leaveEditor();
    if (action.type === "page") {
      setEditing(null);
      setActivePageId(action.pageId);
    }
    if (action.type === "block") setEditing(action.blockId);
    if (action.type === "hide") {
      const currentBlock = pages
        .flatMap((page) => page.blocks || [])
        .find((block) => block.block_id === action.blockId);
      if (currentBlock)
        updateBlock(currentBlock, currentBlock.props, action.visibility);
    }
    if (action.type === "remove") {
      const currentBlock = pages
        .flatMap((page) => page.blocks || [])
        .find((block) => block.block_id === action.blockId);
      if (currentBlock) removeBlock(currentBlock);
    }
    if (action.type === "add") addBlock(action.blockType);
    setPendingAction(null);
  };

  const requestAction = (action, trigger) => {
    if (!hasUnsavedChanges) return performAction(action);
    dialogTrigger.current = trigger || document.activeElement;
    setPendingAction(action);
    setExitOpen(true);
  };

  const saveAndContinue = () => {
    const form = editing
      ? document.getElementById(`website-block-form-${editing}`)
      : null;
    if (!form) return performAction(pendingAction);
    setSaveThenContinue(true);
    form.requestSubmit();
  };

  const discardAndContinue = async () => {
    if (activeUploads > 0) return;
    const abandoned = [...pendingAssetIds];
    setPendingAssetIds([]);
    await Promise.allSettled(
      abandoned.map((fileId) =>
        axios.delete(`${API}/storage/website/assets/${fileId}`, {
          headers: authHeaders(token),
        }),
      ),
    );
    setIsDirty(false);
    setExitOpen(false);
    performAction(pendingAction);
  };

  useEffect(() => {
    if (!saveThenContinue || hasUnsavedChanges || saveState !== "saved") return;
    setSaveThenContinue(false);
    performAction(pendingAction);
  }, [hasUnsavedChanges, pendingAction, saveState, saveThenContinue]);

  const activePage = useMemo(
    () => pages.find((page) => page.page_id === activePageId) || pages[0],
    [activePageId, pages],
  );
  useEffect(() => {
    if (status !== "ready" || typeof window.fetch !== "function") return undefined;
    let active = true;
    const heartbeat = async () => {
      try {
        const response = await window.fetch(
          `${API}/organizations/${user.org_id}/website/collaboration/heartbeat`,
          {
            method: "POST",
            headers: {
              ...authHeaders(token),
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              session_id: editorSessionId.current,
              page_id: activePage?.page_id || null,
              page_title: activePage?.title || null,
              block_id: editing,
            }),
          },
        );
        if (response.ok && active) {
          const data = await response.json();
          setActiveEditors(data.active_editors || []);
        }
      } catch (_error) {
        // Presence is advisory and must never interrupt editing.
      }
    };
    heartbeat();
    const interval = window.setInterval(heartbeat, 30000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [activePage?.page_id, activePage?.title, editing, status, token, user.org_id]);
  const previewPage = historyPreview
    ? historyPreview.snapshot.pages.find((page) => page.stable_key === activePage?.stable_key) || historyPreview.snapshot.pages[0]
    : activePage;
  const canvasTheme = historyPreview?.snapshot?.theme || previewTheme;
  const unpublishedChanges = useMemo(() => {
    if (!site?.current_release_id) return true;
    if (site.draft_diverged_from_release) return true;
    const publishedAt = Date.parse(site.published_at || "");
    if (!Number.isFinite(publishedAt)) return true;
    return [site, ...pages, ...pages.flatMap((page) => page.blocks || [])].some(
      (item) => Date.parse(item.updated_at || "") > publishedAt,
    );
  }, [pages, site]);
  const launchReport = useMemo(
    () => buildWebsiteLaunchReport(pages),
    [pages],
  );
  const openLaunchFinding = (finding) => {
    if (finding.pageId) setActivePageId(finding.pageId);
    setEditing(finding.blockId || null);
    setPageSettingsOpenId(finding.blockId ? null : finding.pageId);
    setMobilePanel("edit");
    setPublishOpen(false);
  };
  const publicUrl = site?.included_slug
    ? site.included_slug === "sjfc"
      ? "https://sjfc.sporthouse.ai"
      : `https://${site.included_slug}.sporthouse.site`
    : null;

  const load = useCallback(async () => {
    setStatus("loading");
    setMessage("");
    try {
      const response = await axios.get(
        `${API}/organizations/${user.org_id}/website`,
        { headers: authHeaders(token) },
      );
      setSite(response.data.site);
      setPreviewTheme({ ...DEFAULT_THEME, ...(response.data.site?.theme || {}) });
      setPages(response.data.pages || []);
      setActivePageId(response.data.pages?.[0]?.page_id || null);
      setStatus(response.data.pages?.length ? "ready" : "empty");
      try {
        setProgramError("");
        const programResponse = await axios.get(
          `${API}/organizations/${user.org_id}/website/programs`,
          { headers: authHeaders(token) },
        );
        setPrograms(programResponse.data.items || []);
      } catch (_programLoadError) {
        setPrograms([]);
        setProgramError(
          "Programs could not be loaded. Content editing remains available; retry before editing program blocks.",
        );
      }
    } catch (error) {
      const code = error.response?.status;
      setStatus(
        code === 402
          ? "payment"
          : code === 403
            ? "forbidden"
            : code === 404
              ? "empty"
              : "error",
      );
      setMessage(
        error.response?.data?.detail?.code ||
          error.response?.data?.detail ||
          "Website Pro could not be loaded.",
      );
    }
  }, [token, user.org_id]);

  useEffect(() => {
    load();
  }, [load]);

  const addStarterSite = async () => {
    setSaveState("saving");
    try {
      let currentSite = site;
      if (!currentSite) {
        const siteResponse = await axios.post(
          `${API}/organizations/${user.org_id}/website`,
          {
            included_slug: `club-${user.org_id.slice(-8)}`,
            template_key: "clubhouse",
          },
          { headers: authHeaders(token) },
        );
        currentSite = siteResponse.data;
      }
      const pageResponse = await axios.post(
        `${API}/organizations/${user.org_id}/website/pages`,
        {
          stable_key: "home",
          route_slug: "home",
          title: "Home",
          status: "draft",
          seo: {},
        },
        { headers: authHeaders(token) },
      );
      setSite(currentSite);
      setPages([{ ...pageResponse.data, blocks: [] }]);
      setActivePageId(pageResponse.data.page_id);
      setStatus("ready");
      setSaveState("saved");
    } catch (error) {
      setSaveState("error");
      setMessage(
        error.response?.data?.detail?.code || "Setup failed. Try again.",
      );
    }
  };

  const createPage = async (event) => {
    event.preventDefault();
    const title = newPageTitle.trim();
    if (!title) return;
    const routeSlug =
      title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || `page-${pages.length + 1}`;
    setSaveState("saving");
    try {
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/website/pages`,
        {
          stable_key: `${routeSlug}-${Date.now().toString(36)}`,
          route_slug: routeSlug,
          title,
          status: "draft",
          seo: {},
          appearance: {},
        },
        { headers: authHeaders(token) },
      );
      const page = { ...response.data, blocks: [] };
      setPages((value) => [...value, page]);
      setSite((value) =>
        value
          ? {
              ...value,
              revision: value.revision + 1,
              page_ids: [...(value.page_ids || []), page.page_id],
            }
          : value,
      );
      setActivePageId(page.page_id);
      setNewPageTitle("");
      setAddingPage(false);
      setSaveState("saved");
    } catch (error) {
      setSaveState("error");
      setMessage(
        error.response?.data?.detail?.code || "Page could not be created.",
      );
    }
  };

  const updatePageSettings = async (updates) => {
    if (!activePage) return;
    setSaveState("saving");
    setMessage("");
    try {
      const response = await axios.patch(
        `${API}/organizations/${user.org_id}/website/pages/${activePage.page_id}`,
        { expected_revision: activePage.revision, ...updates },
        { headers: authHeaders(token) },
      );
      setPages((value) =>
        value.map((page) =>
          page.page_id === activePage.page_id
            ? { ...page, ...response.data, blocks: page.blocks }
            : page,
        ),
      );
      setSaveState("saved");
      setMessage("Page settings saved.");
    } catch (error) {
      setSaveState(error.response?.status === 409 ? "stale" : "error");
      setMessage(
        error.response?.data?.detail?.code === "page_identity_conflict"
          ? "That page URL is already in use. Choose a different slug."
          : "Page settings could not be saved. Reload and try again.",
      );
    }
  };

  const reorderPages = async (direction) => {
    if (!activePage || !site) return;
    const currentIndex = pages.findIndex(
      (page) => page.page_id === activePage.page_id,
    );
    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= pages.length) return;
    const ordered = [...pages];
    [ordered[currentIndex], ordered[nextIndex]] = [
      ordered[nextIndex],
      ordered[currentIndex],
    ];
    setSaveState("saving");
    try {
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/website/pages/reorder`,
        {
          expected_revision: site.revision,
          ordered_ids: ordered.map((page) => page.page_id),
        },
        { headers: authHeaders(token) },
      );
      setPages(ordered);
      setSite(response.data);
      setPreviewTheme({ ...DEFAULT_THEME, ...(response.data.theme || {}) });
      setSaveState("saved");
    } catch (error) {
      setSaveState(error.response?.status === 409 ? "stale" : "error");
      setMessage("Page order could not be saved. Reload and try again.");
    }
  };

  const duplicatePage = async () => {
    if (!activePage || !site) return;
    const suffix = Date.now().toString(36);
    setSaveState("saving");
    try {
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/website/pages/${activePage.page_id}/duplicate`,
        {
          expected_site_revision: site.revision,
          title: `${activePage.title} copy`,
          route_slug: `${activePage.route_slug}-copy-${suffix}`,
          stable_key: `${activePage.stable_key}-copy-${suffix}`,
        },
        { headers: authHeaders(token) },
      );
      const { site: updatedSite, ...page } = response.data;
      setPages((value) => [...value, page]);
      setSite(updatedSite);
      setActivePageId(page.page_id);
      setEditing(null);
      setSaveState("saved");
      setMessage("Page duplicated. Review its name and URL before publishing.");
    } catch (error) {
      setSaveState(error.response?.status === 409 ? "stale" : "error");
      setMessage(
        error.response?.data?.detail?.code === "page_has_attached_assets"
          ? "This page contains uploaded images. Replace or remove those uploads before duplicating it."
          : "Page could not be duplicated. Reload and try again.",
      );
    }
  };

  const deletePage = async () => {
    if (!activePage || !site || activePage.stable_key === "home") return;
    if (!window.confirm(`Delete ${activePage.title}? This cannot be undone.`))
      return;
    setSaveState("saving");
    try {
      const response = await axios.delete(
        `${API}/organizations/${user.org_id}/website/pages/${activePage.page_id}`,
        {
          headers: authHeaders(token),
          data: {
            expected_site_revision: site.revision,
            expected_page_revision: activePage.revision,
          },
        },
      );
      const remaining = pages.filter(
        (page) => page.page_id !== activePage.page_id,
      );
      setPages(remaining);
      setSite(response.data.site);
      setActivePageId(remaining[0]?.page_id || null);
      setEditing(null);
      setSaveState("saved");
      setMessage("Page deleted from the private draft.");
    } catch (error) {
      setSaveState(error.response?.status === 409 ? "stale" : "error");
      setMessage("Page could not be deleted. Reload and try again.");
    }
  };

  const updatePageAppearance = async (appearance) => {
    if (!activePage) return;
    setSaveState("saving");
    try {
      const response = await axios.patch(
        `${API}/organizations/${user.org_id}/website/pages/${activePage.page_id}`,
        { expected_revision: activePage.revision, appearance },
        { headers: authHeaders(token) },
      );
      setPages((value) =>
        value.map((page) =>
          page.page_id === activePage.page_id
            ? { ...page, ...response.data, blocks: page.blocks }
            : page,
        ),
      );
      setSaveState("saved");
    } catch (error) {
      setSaveState(error.response?.status === 409 ? "stale" : "error");
      setMessage("Page colors could not be saved. Reload before trying again.");
    }
  };

  const updateTheme = async (theme) => {
    if (!site) return;
    setSaveState("saving");
    setMessage("");
    try {
      const response = await axios.patch(
        `${API}/organizations/${user.org_id}/website/theme`,
        { expected_revision: site.revision, theme },
        { headers: authHeaders(token) },
      );
      setSite(response.data);
      setPreviewTheme({ ...DEFAULT_THEME, ...(response.data.theme || {}) });
      setSaveState("saved");
      setMessage("Site theme saved to the private draft.");
    } catch (error) {
      setSaveState(error.response?.status === 409 ? "stale" : "error");
      setMessage(
        error.response?.status === 422
          ? "Choose color pairs with stronger contrast before saving."
          : "Theme could not be saved. Reload before trying again.",
      );
    }
  };

  const addBlock = async (type) => {
    if (!activePage) return;
    const props = starterProps(type, programs);
    if (!props) {
      setMessage("Load at least one program before adding this block.");
      return;
    }
    setSaveState("saving");
    try {
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/website/pages/${activePage.page_id}/blocks`,
        {
          type,
          props,
          visibility: "visible",
          style_variant: "default",
        },
        { headers: authHeaders(token) },
      );
      setPages((value) =>
        value.map((page) =>
          page.page_id === activePage.page_id
            ? {
                ...page,
                revision: page.revision + 1,
                blocks: [...(page.blocks || []), response.data],
                block_ids: [...(page.block_ids || []), response.data.block_id],
              }
            : page,
        ),
      );
      setSaveState("saved");
      setEditing(response.data.block_id);
    } catch (error) {
      setSaveState("error");
      setMessage(
        error.response?.data?.detail?.code || "Block could not be added.",
      );
    }
  };

  const loadSavedSections = async () => {
    try {
      const response = await axios.get(
        `${API}/organizations/${user.org_id}/website/saved-sections`,
        { headers: authHeaders(token) },
      );
      setSavedSections(response.data.items || []);
    } catch (_error) {
      // The built-in section library remains available if saved sections fail to load.
    }
  };

  const saveReusableSection = async (block, name) => {
    try {
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/website/blocks/${block.block_id}/saved-sections`,
        { name },
        { headers: authHeaders(token) },
      );
      setSavedSections((items) => [response.data, ...items]);
      setSavingSectionId(null);
      setSavedSectionName("");
      setMessage("Section saved to your organization library.");
    } catch (error) {
      setMessage(error.response?.data?.detail?.code === "website_asset_clone_failed"
        ? "The section image could not be copied safely. Your original section is unchanged."
        : "Section could not be saved for reuse.");
    }
  };

  const renameSavedSection = async (item, name) => {
    try {
      const response = await axios.patch(
        `${API}/organizations/${user.org_id}/website/saved-sections/${item.saved_section_id}`,
        { expected_revision: item.revision, name },
        { headers: authHeaders(token) },
      );
      setSavedSections((items) => items.map((value) => value.saved_section_id === item.saved_section_id ? response.data : value));
    } catch (error) {
      setMessage(error.response?.status === 409 ? "That saved section changed. Reopen the library and try again." : "Saved section could not be renamed.");
    }
  };

  const deleteSavedSection = async (item) => {
    try {
      await axios.delete(
        `${API}/organizations/${user.org_id}/website/saved-sections/${item.saved_section_id}`,
        { headers: authHeaders(token) },
      );
      setSavedSections((items) => items.filter((value) => value.saved_section_id !== item.saved_section_id));
    } catch (_error) {
      setMessage("Saved section could not be deleted.");
    }
  };

  const addSavedSection = async (savedSection) => {
    if (!activePage) return;
    setSaveState("saving");
    try {
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/website/pages/${activePage.page_id}/saved-sections/${savedSection.saved_section_id}`,
        { expected_page_revision: activePage.revision },
        { headers: authHeaders(token) },
      );
      setPages((items) => items.map((page) => page.page_id === activePage.page_id
        ? { ...page, revision: page.revision + 1, blocks: [...page.blocks, response.data], block_ids: [...page.block_ids, response.data.block_id] }
        : page));
      setEditing(response.data.block_id);
      setLibraryOpen(false);
      setSaveState("saved");
    } catch (error) {
      setSaveState(error.response?.status === 409 ? "stale" : "error");
      setMessage("Reusable section could not be added. Reload before trying again.");
    }
  };

  const duplicateBlock = async (block) => {
    if (!activePage || assetIdsIn(block.props).length > 0) return;
    setSaveState("saving");
    try {
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/website/pages/${activePage.page_id}/blocks`,
        {
          type: block.type,
          props: block.props,
          visibility: block.visibility,
          style_variant: block.style_variant || "default",
          appearance: block.appearance || {},
        },
        { headers: authHeaders(token) },
      );
      setPages((value) =>
        value.map((page) =>
          page.page_id === activePage.page_id
            ? {
                ...page,
                revision: page.revision + 1,
                blocks: [...(page.blocks || []), response.data],
                block_ids: [...(page.block_ids || []), response.data.block_id],
              }
            : page,
        ),
      );
      setEditing(response.data.block_id);
      setSaveState("saved");
      setMessage(
        `${BLOCK_LABELS[block.type] || "Section"} duplicated at the end of the page.`,
      );
    } catch (error) {
      setSaveState(error.response?.status === 409 ? "stale" : "error");
      setMessage("This section could not be duplicated. Reload and try again.");
    }
  };

  const updateBlock = async (
    block,
    props,
    visibility = block.visibility,
    appearance = block.appearance || {},
  ) => {
    setFieldError("");
    if (block.type === "hero" && !props.headline?.trim()) {
      setFieldError("Headline is required.");
      return false;
    }
    setSaveState("saving");
    try {
      const response = await axios.patch(
        `${API}/organizations/${user.org_id}/website/blocks/${block.block_id}`,
        { expected_revision: block.revision, props, visibility, appearance },
        { headers: authHeaders(token) },
      );
      setPages((value) =>
        value.map((page) => ({
          ...page,
          blocks: (page.blocks || []).map((item) =>
            item.block_id === block.block_id ? response.data : item,
          ),
        })),
      );
      const savedAssetIds = new Set(assetIdsIn(props));
      setPendingAssetIds((ids) => ids.filter((id) => !savedAssetIds.has(id)));
      setSaveState("saved");
      requestAnimationFrame(() => lastControl.current?.focus());
      return true;
    } catch (error) {
      if (error.response?.status === 409) {
        setSaveState("stale");
        setMessage(
          "This page changed in another session. Reload to keep the newest version.",
        );
      } else {
        setSaveState("error");
        setFieldError(
          error.response?.data?.detail?.fields?.join(", ") ||
            "Changes could not be saved.",
        );
      }
      return false;
    }
  };

  const reorder = async (index, direction) => {
    const blocks = [...(activePage.blocks || [])];
    const next = index + direction;
    if (next < 0 || next >= blocks.length) return;
    [blocks[index], blocks[next]] = [blocks[next], blocks[index]];
    setSaveState("saving");
    try {
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/website/pages/${activePage.page_id}/blocks/reorder`,
        {
          expected_revision: activePage.revision,
          ordered_ids: blocks.map((block) => block.block_id),
        },
        { headers: authHeaders(token) },
      );
      setPages((value) =>
        value.map((page) =>
          page.page_id === activePage.page_id
            ? {
                ...page,
                revision: response.data.revision,
                block_ids: response.data.block_ids,
                blocks,
              }
            : page,
        ),
      );
      setSaveState("saved");
    } catch (error) {
      setSaveState(error.response?.status === 409 ? "stale" : "error");
      setMessage("Order could not be saved. Reload before trying again.");
    }
  };

  const removeBlock = async (block) => {
    if (
      !window.confirm(
        "Remove this block from the private draft? This cannot be undone.",
      )
    )
      return;
    setSaveState("saving");
    try {
      const response = await axios.delete(
        `${API}/organizations/${user.org_id}/website/blocks/${block.block_id}`,
        {
          headers: authHeaders(token),
          data: {
            expected_revision: block.revision,
            expected_page_revision: activePage.revision,
          },
        },
      );
      setPages((value) =>
        value.map((page) =>
          page.page_id === activePage.page_id
            ? {
                ...page,
                revision: response.data.page.revision,
                block_ids: response.data.page.block_ids,
                blocks: page.blocks.filter(
                  (item) => item.block_id !== block.block_id,
                ),
              }
            : page,
        ),
      );
      setEditing(null);
      setSaveState("saved");
    } catch (error) {
      setSaveState(error.response?.status === 409 ? "stale" : "error");
      setMessage("Block could not be removed. Reload before trying again.");
    }
  };

  const publish = async () => {
    if (!site || hasUnsavedChanges || publishing) return;
    setPublishing(true);
    setMessage("");
    try {
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/website/publish`,
        { expected_revision: site.revision },
        { headers: authHeaders(token) },
      );
      setSite(response.data.site);
      setPublishOpen(false);
      setMessage("Your website is live with the latest saved changes.");
    } catch (error) {
      if (error.response?.status === 409) setSaveState("stale");
      setMessage(
        friendlyWebsiteError(
          error,
          "The website could not be published. Your current live version is unchanged.",
        ),
      );
      setPublishOpen(false);
    } finally {
      setPublishing(false);
    }
  };

  const unpublish = async () => {
    if (!site || unpublishing) return;
    setUnpublishing(true);
    setMessage("");
    try {
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/website/unpublish`,
        { expected_revision: site.revision },
        { headers: authHeaders(token) },
      );
      setSite(response.data.site);
      setUnpublishOpen(false);
      setMessage("Your website is offline. The private draft and release history are preserved.");
    } catch (error) {
      if (error.response?.status === 409) setSaveState("stale");
      setMessage(
        friendlyWebsiteError(
          error,
          "The website could not be taken offline. Reload before trying again.",
        ),
      );
      setUnpublishOpen(false);
    } finally {
      setUnpublishing(false);
    }
  };

  if (status !== "ready")
    return (
      <PageLayout title="Website Pro">
        <div className="mx-auto max-w-3xl rounded-[28px] border border-[#dfe6df] bg-white p-8 text-center">
          {status === "loading" ? (
            <>
              <Loader2 className="mx-auto h-7 w-7 animate-spin" />
              <p className="mt-4">Loading Website Pro…</p>
            </>
          ) : (
            <>
              <AlertTriangle className="mx-auto h-8 w-8 text-[#9b4b35]" />
              <h1 className="mt-4 text-2xl font-bold">
                {status === "payment"
                  ? "Website Pro subscription required"
                  : status === "forbidden"
                    ? "Organization admin access required"
                    : status === "empty"
                      ? "Build your Clubhouse site"
                      : "Website Pro is unavailable"}
              </h1>
              <p className="mx-auto mt-2 max-w-xl text-[#60706a]">
                {message ||
                  (status === "empty"
                    ? "Start with a private draft. Nothing becomes public in this step."
                    : "Check your access or try again.")}
              </p>
              <div className="mt-6 flex justify-center gap-3">
                {status === "empty" && (
                  <Button onClick={addStarterSite}>Create private draft</Button>
                )}
                {status === "payment" && (
                  <Button asChild>
                    <a href="/admin/marketplace">Open Marketplace</a>
                  </Button>
                )}
                <Button variant="outline" onClick={load}>
                  Retry
                </Button>
              </div>
            </>
          )}
        </div>
      </PageLayout>
    );

  return (
    <div
      className="flex min-h-dvh flex-col bg-[#e9eeea] text-[#102a27]"
      data-testid="website-pro-editor"
    >
      <header className="sticky top-0 z-40 flex min-h-20 flex-col gap-4 border-b border-white/10 bg-[#0b211e] px-4 py-3 text-white shadow-[0_12px_35px_rgba(11,33,30,0.2)] sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#b7f34a] text-lg font-black text-[#102a27] sm:flex"
            aria-hidden="true"
          >
            W
          </div>
          <div className="min-w-0">
            <p className="truncate text-xs font-bold uppercase tracking-[.2em] text-[#b7f34a]">
              Clubhouse · Design studio · Private draft
            </p>
            <h1 className="mt-0.5 truncate text-xl font-semibold tracking-tight">
              Website editor
            </h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {activeEditors.length > 0 && (
            <details className="relative">
              <summary className="flex min-h-9 cursor-pointer list-none items-center rounded-full border border-amber-200/30 bg-amber-200/10 px-3 text-xs font-bold text-amber-100">
                <Users className="mr-1.5 h-3.5 w-3.5" />
                {activeEditors.length} {activeEditors.length === 1 ? "other editor" : "other editors"}
              </summary>
              <div className="absolute right-0 z-50 mt-2 w-64 rounded-2xl border border-[#dce4dc] bg-white p-3 text-[#102a27] shadow-xl">
                <p className="text-xs font-bold uppercase tracking-wider text-[#60706a]">Editing now</p>
                <ul className="mt-2 space-y-2">
                  {activeEditors.map((editor) => (
                    <li key={editor.session_id} className="rounded-xl bg-[#f4f7f3] p-2 text-xs">
                      <strong>{editor.editor_name}</strong>
                      <span className="mt-1 block text-[#60706a]">
                        {editor.page_title ? `${editor.page_title}${editor.block_id ? " · section open" : ""}` : "Website settings"}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[10px] leading-4 text-[#60706a]">Changes still use revision checks. Reload before saving if a newer version appears.</p>
              </div>
            </details>
          )}
          <span
            className={`mr-1 inline-flex min-h-9 items-center rounded-full border px-3 text-xs font-bold ${saveState === "saved" ? "border-[#76d9ca]/30 bg-[#76d9ca]/10 text-[#a9efe4]" : saveState === "stale" ? "border-amber-300/30 bg-amber-300/10 text-amber-100" : "border-white/15 bg-white/10 text-white"}`}
            aria-live="polite"
          >
            {saveState === "saving"
              ? "Saving…"
              : saveState === "saved"
                ? "Saved"
                : saveState === "stale"
                  ? "Newer changes available"
                  : "Save error"}
          </span>
          {saveState === "stale" && (
            <div className="flex items-center gap-2">
              <span className="hidden max-w-56 text-xs leading-4 text-amber-100 xl:inline">
                Another editor saved first. Copy any unsaved text before reloading.
              </span>
              <Button variant="outline" onClick={load}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reload latest
              </Button>
            </div>
          )}
          <div
            className="flex rounded-full border border-white/20 bg-white/10 p-1"
            role="group"
            aria-label="Preview size"
          >
            {[
              ["desktop", "Desktop", Monitor],
              ["tablet", "Tablet", Tablet],
              ["mobile", "Mobile", Smartphone],
            ].map(([value, label, Icon]) => (
              <button
                key={value}
                type="button"
                aria-label={`Preview ${label.toLowerCase()}`}
                aria-pressed={viewport === value}
                onClick={() => setViewport(value)}
                className={`flex min-h-9 items-center rounded-full px-3 text-xs font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b7f34a] ${viewport === value ? "bg-white text-[#102a27] shadow-sm" : "text-white hover:bg-white/10"}`}
              >
                <Icon className="mr-1.5 h-3.5 w-3.5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
          {site?.current_release_id && publicUrl && (
            <Button
              variant="ghost"
              asChild
              className="rounded-full text-white hover:bg-white/10 hover:text-white"
            >
              <a href={publicUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                View live
              </a>
            </Button>
          )}
          {site?.status === "published" && site?.current_release_id && (
            <Button
              variant="ghost"
              onClick={() => setUnpublishOpen(true)}
              className="rounded-full text-amber-100 hover:bg-white/10 hover:text-amber-50"
            >
              <EyeOff className="mr-2 h-4 w-4" />
              Take offline
            </Button>
          )}
          <Button
            disabled={hasUnsavedChanges || saveState !== "saved" || !unpublishedChanges}
            title={
              hasUnsavedChanges
                ? "Save the open section before publishing"
                : !unpublishedChanges
                  ? "The live website already has the latest changes"
                  : undefined
            }
            onClick={() => setPublishOpen(true)}
            className="rounded-full bg-[#b7f34a] text-[#102a27] disabled:bg-[#b7f34a] disabled:text-[#102a27]"
          >
            {site?.current_release_id ? "Publish changes" : "Publish website"}
          </Button>
          <Button
            ref={closeButton}
            variant="ghost"
            size="icon"
            onClick={(event) =>
              requestAction({ type: "exit" }, event.currentTarget)
            }
            aria-label="Close editor"
            title="Close editor"
            className="h-11 w-11 rounded-full text-white hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </header>
      <div
        className="flex border-b border-[#d7e0d8] bg-[#f8faf6] p-2 lg:hidden"
        role="tablist"
        aria-label="Editor workspace"
      >
        {[
          ["edit", "Edit site"],
          ["preview", "Preview"],
        ].map(([value, label]) => (
          <button
            key={value}
            role="tab"
            aria-selected={mobilePanel === value}
            onClick={() => setMobilePanel(value)}
            className={`min-h-11 flex-1 rounded-xl px-4 text-sm font-semibold transition ${mobilePanel === value ? "bg-[#102a27] text-white shadow-sm" : "text-[#52635d] hover:bg-[#edf2ed]"}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div
        className="grid min-h-0 flex-1 lg:grid-cols-[240px_minmax(0,1fr)_390px]"
        data-testid="website-pro-workspace"
      >
        <div
          className={`${mobilePanel === "edit" ? "block" : "hidden"} overflow-y-auto border-r border-[#d7e0d8] bg-[#f4f7f3] p-3 sm:p-4 lg:contents`}
        >
          <div className="grid gap-4 lg:contents">
            <nav
              className="h-fit rounded-[24px] border border-[#dce4dc] bg-[#fbfcf8] p-4 shadow-[0_10px_30px_rgba(16,42,39,0.05)] lg:col-start-1 lg:row-start-1 lg:m-4 lg:max-h-[calc(100dvh-7rem)] lg:overflow-auto"
              aria-label="Website pages"
            >
              <p className="text-[11px] font-bold uppercase tracking-[.18em] text-[#087f67]">
                Site structure
              </p>
              <h2 className="mt-1 text-lg font-semibold tracking-tight text-[#102a27]">
                Pages
              </h2>
              {pages.map((page) => (
                <button
                  key={page.page_id}
                  onClick={(event) =>
                    requestAction(
                      { type: "page", pageId: page.page_id },
                      event.currentTarget,
                    )
                  }
                  className={`mt-3 flex min-h-12 w-full items-center justify-between rounded-2xl px-3.5 py-3 text-left text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#087f67] ${page.page_id === activePage?.page_id ? "bg-[#102a27] text-white shadow-sm" : "text-[#42534d] hover:bg-[#edf3ed]"}`}
                >
                  {page.title}
                  <ChevronRight className="h-4 w-4" />
                </button>
              ))}
              {activePage && (
                <PageSettings
                  key={activePage.page_id}
                  page={activePage}
                  site={site}
                  index={pages.findIndex(
                    (page) => page.page_id === activePage.page_id,
                  )}
                  total={pages.length}
                  onSave={updatePageSettings}
                  onMove={reorderPages}
                  onDuplicate={duplicatePage}
                  onDelete={deletePage}
                  forceOpen={pageSettingsOpenId === activePage.page_id}
                />
              )}
              <details className="mt-4 rounded-2xl border border-[#c8d4ca] bg-white p-3">
                <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-[#344740]">
                  <Palette className="h-4 w-4 text-[#087f67]" />
                  Site theme
                </summary>
                <p className="mt-2 text-xs leading-5 text-[#60706a]">
                  Shared colors, type, buttons, spacing, and width for every page.
                </p>
                <ThemeControls
                  value={site?.theme}
                  onSave={updateTheme}
                  onPreview={setPreviewTheme}
                  saving={saveState === "saving"}
                />
              </details>
              <WebsiteInquiryInbox orgId={user.org_id} token={token} />
              <WebsiteAnalytics orgId={user.org_id} token={token} />
              <WebsiteOperationalHealth orgId={user.org_id} token={token} refreshKey={`${site?.current_release_id || "offline"}-${site?.revision || 0}`} />
              <WebsiteDomainControls orgId={user.org_id} token={token} />
              <WebsitePublishingSchedule orgId={user.org_id} token={token} site={site} disabled={hasUnsavedChanges || saveState !== "saved"} />
              <WebsiteReleaseHistory
                orgId={user.org_id}
                token={token}
                site={site}
                onPreview={(release) => {
                  setHistoryPreview(release);
                  setMobilePanel("preview");
                }}
                onRestored={(updatedSite) => {
                  setSite(updatedSite);
                  setHistoryPreview(null);
                  setMessage("An earlier version is live. Your saved draft remains unchanged.");
                }}
              />
              {addingPage ? (
                <form
                  onSubmit={createPage}
                  className="mt-3 rounded-2xl border border-[#c8d4ca] bg-white p-3"
                >
                  <label
                    className="text-xs font-bold text-[#42534d]"
                    htmlFor="website-new-page-title"
                  >
                    Page name
                  </label>
                  <input
                    id="website-new-page-title"
                    autoFocus
                    value={newPageTitle}
                    onChange={(event) => setNewPageTitle(event.target.value)}
                    placeholder="About us"
                    maxLength={160}
                    className="mt-2 min-h-11 w-full rounded-xl border border-[#cbd6ce] px-3 text-sm outline-none focus:border-[#087f67] focus:ring-2 focus:ring-[#087f67]/20"
                  />
                  <div className="mt-3 flex gap-2">
                    <Button
                      type="submit"
                      size="sm"
                      disabled={!newPageTitle.trim()}
                    >
                      Add page
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setAddingPage(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <Button
                  variant="outline"
                  className="mt-3 w-full rounded-2xl border-dashed border-[#9fb0a2]"
                  onClick={() => setAddingPage(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add page
                </Button>
              )}
              <p className="mt-5 rounded-2xl bg-[#edf3ed] p-3 text-xs leading-5 text-[#60706a]">
                Each page has its own address, content blocks, and color
                palette.
              </p>
            </nav>
            <section className="rounded-[24px] border border-[#dce4dc] bg-[#fbfcf8] p-4 shadow-[0_10px_30px_rgba(16,42,39,0.05)] sm:p-5 lg:col-start-3 lg:row-start-1 lg:m-4 lg:max-h-[calc(100dvh-7rem)] lg:overflow-auto">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[.18em] text-[#087f67]">
                    Section settings
                  </p>
                  <h2 className="mt-1 text-lg font-semibold tracking-tight">
                    {editing
                      ? BLOCK_LABELS[
                          activePage?.blocks?.find(
                            (block) => block.block_id === editing,
                          )?.type
                        ] || "Selected section"
                      : activePage?.title}
                  </h2>
                </div>
                <span className="rounded-full bg-[#edf3ed] px-3 py-1.5 text-xs font-semibold text-[#60706a]">
                  {editing
                    ? "Selected on canvas"
                    : `${activePage?.blocks?.length || 0} blocks`}
                </span>
              </div>
              <details className="mt-5 rounded-2xl border border-[#dce4dc] bg-[#f4f7f3] p-3">
                <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-[#344740]">
                  <Palette className="h-4 w-4 text-[#087f67]" />
                  Page colors
                </summary>
                <div className="mt-3">
                  <AppearanceControls
                    label={`${activePage?.title || "Page"} page`}
                    value={activePage?.appearance}
                    onChange={updatePageAppearance}
                  />
                </div>
              </details>
              <div className="mt-5 rounded-2xl border border-[#c8d4ca] bg-white p-3">
                <Button
                  variant="outline"
                  className="w-full justify-between rounded-xl border-[#b8c8bc]"
                  aria-expanded={libraryOpen}
                  onClick={() => {
                    if (!libraryOpen) loadSavedSections();
                    setLibraryOpen((value) => !value);
                  }}
                >
                  <span className="flex items-center">
                    <Plus className="mr-2 h-4 w-4" />
                    Add a section
                  </span>
                  <span className="text-xs text-[#60706a]">
                    {Object.keys(BLOCK_LABELS).length} choices
                  </span>
                </Button>
                {libraryOpen && (
                  <div className="mt-4 space-y-5" aria-label="Section library">
                    {savedSections.length > 0 && (
                      <section>
                        <h3 className="text-xs font-bold uppercase tracking-[.14em] text-[#087f67]">Saved by your organization</h3>
                        <div className="mt-2 grid gap-2">
                          {savedSections.map((item) => (
                            <SavedSectionCard
                              key={item.saved_section_id}
                              item={item}
                              onAdd={addSavedSection}
                              onRename={renameSavedSection}
                              onDelete={deleteSavedSection}
                            />
                          ))}
                        </div>
                      </section>
                    )}
                    {BLOCK_LIBRARY.map((group) => (
                      <section key={group.category}>
                        <h3 className="text-xs font-bold uppercase tracking-[.14em] text-[#087f67]">
                          {group.category}
                        </h3>
                        <p className="mt-1 text-xs leading-5 text-[#60706a]">
                          {group.description}
                        </p>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                          {Object.entries(group.blocks).map(
                            ([type, description]) => (
                              <button
                                key={type}
                                className="group flex min-h-20 w-full items-stretch overflow-hidden rounded-xl border border-[#d6dfd7] bg-[#fbfcf8] text-left transition hover:border-[#087f67] hover:bg-[#f1faf5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#087f67]"
                                aria-label={`Add ${BLOCK_LABELS[type]}`}
                                onClick={(event) => {
                                  requestAction(
                                    { type: "add", blockType: type },
                                    event.currentTarget,
                                  );
                                  setLibraryOpen(false);
                                }}
                              >
                                <span
                                  className="flex w-20 shrink-0 items-center justify-center bg-[#e6eee7] p-3 group-hover:bg-[#dff3e7]"
                                  aria-hidden="true"
                                >
                                  <span className="grid h-10 w-12 gap-1 rounded border border-[#9db1a1] bg-white p-1.5">
                                    <span className="h-1.5 w-3/4 rounded-full bg-[#102a27]" />
                                    <span className="h-1 rounded-full bg-[#98aaa0]" />
                                    <span className="h-1 rounded-full bg-[#b7f34a]" />
                                  </span>
                                </span>
                                <span className="p-3">
                                  <strong className="block text-sm text-[#102a27]">
                                    {BLOCK_LABELS[type]}
                                  </strong>
                                  <span className="mt-1 block text-xs leading-4 text-[#60706a]">
                                    {description}
                                  </span>
                                </span>
                              </button>
                            ),
                          )}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </div>
              <ol className="mt-5 space-y-3">
                {(activePage?.blocks || []).map((block, index) => (
                  <li
                    key={block.block_id}
                    className={`rounded-[20px] border bg-white p-3 transition ${editing === block.block_id ? "border-[#76cdbd] shadow-[0_12px_30px_rgba(8,127,103,0.1)] ring-1 ring-[#76cdbd]" : "border-[#dce4dc] hover:border-[#bdccc0]"}`}
                  >
                    <div className="flex items-center gap-2">
                      <button
                        ref={editing === block.block_id ? lastControl : null}
                        className="min-h-11 min-w-0 flex-1 rounded-xl px-2 text-left text-sm font-semibold text-[#102a27] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#087f67]"
                        onClick={(event) =>
                          requestAction(
                            {
                              type: "block",
                              blockId:
                                editing === block.block_id
                                  ? null
                                  : block.block_id,
                            },
                            event.currentTarget,
                          )
                        }
                        aria-expanded={editing === block.block_id}
                      >
                        {BLOCK_LABELS[block.type] || block.type}
                      </button>
                      <button
                        aria-label={`Save ${BLOCK_LABELS[block.type]} as reusable section`}
                        title="Save to organization library"
                        onClick={() => {
                          setSavingSectionId(block.block_id);
                          setSavedSectionName(`${BLOCK_LABELS[block.type] || "Reusable"} section`);
                        }}
                        className="flex h-10 w-10 items-center justify-center rounded-xl text-[#60706a] hover:bg-[#edf3ed]"
                      >
                        <BookmarkPlus className="h-4 w-4" />
                      </button>
                      <button
                        aria-label={`Move ${BLOCK_LABELS[block.type]} up`}
                        onClick={() => reorder(index, -1)}
                        disabled={index === 0}
                        className="flex h-10 w-10 items-center justify-center rounded-xl text-[#60706a] hover:bg-[#edf3ed] disabled:opacity-30"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        aria-label={`Move ${BLOCK_LABELS[block.type]} down`}
                        onClick={() => reorder(index, 1)}
                        disabled={index === activePage.blocks.length - 1}
                        className="flex h-10 w-10 items-center justify-center rounded-xl text-[#60706a] hover:bg-[#edf3ed] disabled:opacity-30"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                      <button
                        aria-label={`Duplicate ${BLOCK_LABELS[block.type]}`}
                        title={
                          assetIdsIn(block.props).length > 0
                            ? "Sections with uploaded images cannot be duplicated yet"
                            : "Duplicate section"
                        }
                        onClick={() => duplicateBlock(block)}
                        disabled={assetIdsIn(block.props).length > 0}
                        className="flex h-10 w-10 items-center justify-center rounded-xl text-[#60706a] hover:bg-[#edf3ed] disabled:cursor-not-allowed disabled:opacity-35"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        aria-label={`${block.visibility === "hidden" ? "Show" : "Hide"} ${BLOCK_LABELS[block.type]}`}
                        onClick={(event) =>
                          requestAction(
                            {
                              type: "hide",
                              blockId: block.block_id,
                              visibility:
                                block.visibility === "hidden"
                                  ? "visible"
                                  : "hidden",
                            },
                            event.currentTarget,
                          )
                        }
                        className="flex h-10 w-10 items-center justify-center rounded-xl text-[#60706a] hover:bg-[#edf3ed]"
                      >
                        {block.visibility === "hidden" ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        aria-label={`Remove ${BLOCK_LABELS[block.type]}`}
                        onClick={(event) =>
                          requestAction(
                            { type: "remove", blockId: block.block_id },
                            event.currentTarget,
                          )
                        }
                        className="flex h-10 w-10 items-center justify-center rounded-xl text-[#60706a] hover:bg-[#fff3ef] hover:text-[#9b3e2f]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {savingSectionId === block.block_id && (
                      <form
                        className="mt-3 flex flex-col gap-2 rounded-xl bg-[#f4f7f3] p-3"
                        onSubmit={(event) => {
                          event.preventDefault();
                          if (savedSectionName.trim()) saveReusableSection(block, savedSectionName.trim());
                        }}
                      >
                        <label className="text-xs font-bold text-[#42534d]">
                          Reusable section name
                          <input
                            autoFocus
                            value={savedSectionName}
                            maxLength={80}
                            onChange={(event) => setSavedSectionName(event.target.value)}
                            className="mt-1 min-h-10 w-full rounded-lg border border-[#b9c9bd] bg-white px-3 text-sm"
                          />
                        </label>
                        <div className="flex gap-2">
                          <Button size="sm" type="submit" disabled={!savedSectionName.trim()}>Save section</Button>
                          <Button size="sm" type="button" variant="outline" onClick={() => setSavingSectionId(null)}>Cancel</Button>
                        </div>
                      </form>
                    )}
                    <details className="mt-3 border-t border-[#edf1ed] pt-3">
                      <summary className="flex cursor-pointer list-none items-center gap-2 text-xs font-semibold text-[#52635d]">
                        <Palette className="h-3.5 w-3.5 text-[#087f67]" />
                        Section colors
                      </summary>
                      <div className="mt-3">
                        <AppearanceControls
                          label={BLOCK_LABELS[block.type] || "Section"}
                          value={block.appearance}
                          onChange={(appearance) =>
                            updateBlock(
                              block,
                              block.props,
                              block.visibility,
                              appearance,
                            )
                          }
                        />
                      </div>
                    </details>
                    <details className="mt-3 border-t border-[#edf1ed] pt-3">
                      <summary className="flex cursor-pointer list-none items-center gap-2 text-xs font-semibold text-[#52635d]">
                        <Smartphone className="h-3.5 w-3.5 text-[#087f67]" />
                        Mobile layout
                      </summary>
                      <p className="mt-2 text-xs leading-5 text-[#60706a]">
                        These overrides apply below the tablet breakpoint.
                      </p>
                      <div className="mt-3">
                        <MobileAppearanceControls
                          label={BLOCK_LABELS[block.type] || "Section"}
                          value={block.appearance}
                          onChange={(appearance) =>
                            updateBlock(
                              block,
                              block.props,
                              block.visibility,
                              appearance,
                            )
                          }
                        />
                      </div>
                    </details>
                    {editing === block.block_id && (
                      <WebsiteBlockEditor
                        block={block}
                        programs={programs}
                        error={fieldError}
                        onSave={(props) => updateBlock(block, props)}
                        onDirtyChange={setIsDirty}
                        onUploadStateChange={(uploading) =>
                          setActiveUploads((count) =>
                            Math.max(0, count + (uploading ? 1 : -1)),
                          )
                        }
                        onAssetUploaded={(fileId) =>
                          fileId &&
                          setPendingAssetIds((ids) =>
                            ids.includes(fileId) ? ids : [...ids, fileId],
                          )
                        }
                      />
                    )}
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </div>
        <section
          className={`${mobilePanel === "preview" ? "block" : "hidden"} min-w-0 overflow-auto bg-[#dfe6df] p-4 sm:p-6 lg:col-start-2 lg:row-start-1 lg:block lg:h-[calc(100dvh-5rem)] lg:p-8`}
        >
          <div className="mb-5 flex items-end justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[.18em] text-[#087f67]">
                Live canvas
              </p>
              <h2 className="mt-1 text-lg font-semibold tracking-tight">
                {historyPreview ? `Version ${historyPreview.sequence} preview` : "Responsive preview"}
              </h2>
            </div>
            <span className="rounded-full border border-[#c7d3c9] bg-[#f8faf6] px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#52635d]">
              {historyPreview
                ? "Historical · read only"
                : site?.current_release_id
                ? unpublishedChanges
                  ? `Draft changes · ${viewport}`
                  : `Published · ${viewport}`
                : `Private draft · ${viewport}`}
            </span>
            {historyPreview && (
              <Button size="sm" variant="outline" onClick={() => setHistoryPreview(null)}>Return to draft</Button>
            )}
          </div>
          <div
            className={
              viewport === "mobile"
                ? "overflow-visible"
                : "overflow-x-auto pb-3"
            }
          >
            <ClubhousePreview
              page={previewPage}
              theme={canvasTheme}
              programs={programs}
              viewport={viewport}
              selectedBlockId={historyPreview ? null : editing}
              onSelectBlock={(blockId) =>
                !historyPreview && requestAction({ type: "block", blockId })
              }
            />
          </div>
        </section>
      </div>
      {(message || programError) && (
        <div
          role="status"
          className="rounded-xl border border-[#e6c780] bg-[#fff8df] p-3 text-sm"
        >
          {message || programError}
        </div>
      )}
      <AlertDialog open={publishOpen} onOpenChange={setPublishOpen}>
        <AlertDialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {site?.current_release_id
                ? "Publish saved changes?"
                : "Publish this website?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pages.length} {pages.length === 1 ? "page" : "pages"} will be
              included in a new live version. If publishing fails, the current
              live website will remain unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <section
            className={`rounded-2xl border p-4 ${launchReport.ready ? "border-[#b8d8c8] bg-[#f1faf5]" : "border-amber-300 bg-amber-50"}`}
            aria-label="Launch readiness"
          >
            <div className="flex items-start gap-3">
              {launchReport.ready ? (
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#087f67]" />
              ) : (
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
              )}
              <div>
                <h3 className="font-semibold text-[#102a27]">
                  {launchReport.ready
                    ? "No launch blockers found"
                    : `${launchReport.blockers.length} launch ${launchReport.blockers.length === 1 ? "blocker" : "blockers"}`}
                </h3>
                <p className="mt-1 text-sm leading-6 text-[#52635d]">
                  {launchReport.ready
                    ? launchReport.warnings.length
                      ? `You can publish now. Review ${launchReport.warnings.length} optional improvement${launchReport.warnings.length === 1 ? "" : "s"} when practical.`
                      : "This saved draft passed the content, link, SEO, and accessibility checks."
                    : "Resolve the items marked Required before publishing. Your current live site will not change."}
                </p>
              </div>
            </div>
            {launchReport.checks.length > 0 && (
              <ul className="mt-4 space-y-2">
                {launchReport.checks.map((check) => (
                  <li
                    key={check.id}
                    className="flex items-start justify-between gap-3 rounded-xl border border-black/10 bg-white p-3"
                  >
                    <div className="flex min-w-0 items-start gap-2">
                      {check.severity === "blocker" ? (
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                      ) : (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#087f67]" />
                      )}
                      <div>
                        <p className="text-sm font-semibold text-[#102a27]">
                          {check.title}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-[#60706a]">
                          {check.severity === "blocker" ? "Required" : "Recommended"} · {check.detail}
                        </p>
                      </div>
                    </div>
                    {check.pageId && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        aria-label={`Fix ${check.title}`}
                        onClick={() => openLaunchFinding(check)}
                      >
                        Fix
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              disabled={publishing || !launchReport.ready}
              onClick={publish}
            >
              {publishing ? "Publishing…" : "Publish now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={unpublishOpen} onOpenChange={setUnpublishOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Take this website offline?</AlertDialogTitle>
            <AlertDialogDescription>
              Visitors will immediately lose access at both the included and custom addresses.
              Your private draft, connected domain, analytics, inquiries, and complete release
              history will be preserved so you can publish again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unpublishing}>Keep website live</AlertDialogCancel>
            <AlertDialogAction disabled={unpublishing} onClick={unpublish}>
              {unpublishing ? "Taking offline…" : "Take offline now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={exitOpen} onOpenChange={setExitOpen}>
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            (dialogTrigger.current || closeButton.current)?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              Save changes before{" "}
              {pendingAction?.type === "exit" ? "leaving" : "continuing"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This block has changes that have not been saved. Discarding them
              cannot be undone, and closing the editor never publishes your
              website.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingAction(null)}>
              Keep editing
            </AlertDialogCancel>
            <Button
              variant="outline"
              disabled={activeUploads > 0}
              onClick={discardAndContinue}
            >
              Discard
            </Button>
            <AlertDialogAction
              disabled={activeUploads > 0}
              onClick={saveAndContinue}
            >
              {pendingAction?.type === "exit"
                ? "Save and exit"
                : "Save and continue"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminWebsitePro;
